import {
  Component,
  DestroyRef,
  Injector,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import {
  Observable,
  catchError,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs';

import {
  EntityInterface,
  EntitySummaryType,
  TableColCfg
} from '@gfe/core/models/entities/entity-visualization-interface';
import { matchDate } from '@gfe/core/models/entities/implementations/recurrence';
import {
  SiteSchedule,
  isScheduleCompatibleWithDelivery,
  isScheduleCompatibleWithPickup
} from '@gfe/core/models/entities/implementations/site-schedule';
import {
  DateTimeWindow,
  EarlinessCfg,
  LatenessCfg,
  makeStartAndFinishTimeValues
} from '@gfe/core/models/entities/implementations/time-window';
import {
  UserCommodityCfg,
  UserCommodityV1Model
} from '@gfe/core/models/entities/implementations/user-commodity-v1';
import {
  UserFlowKindCfg,
  UserFlowKindV1Model
} from '@gfe/core/models/entities/implementations/user-flow-kind-v1';
import {
  UserJobKindCfg,
  UserJobKindV1Model
} from '@gfe/core/models/entities/implementations/user-job-kind-v1';
import {
  Operation,
  UserJobTransportOrderV1InputModel,
  UserJobTransportOrderV1Model,
  UserVehicleRegistrationAssignment
} from '@gfe/core/models/entities/implementations/user-job-transport-order-v1';
import {
  UserSiteV1Model,
  siteKindToString
} from '@gfe/core/models/entities/implementations/user-site-v1';
import {
  UserVehicleCfg,
  UserVehicleV1Model
} from '@gfe/core/models/entities/implementations/user-vehicle-v1';
import { EntitySelectorRequestModel } from '@gfe/core/models/entity/entity-selector-request.model';
import { GbeErrorCodes } from '@gfe/core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@gfe/core/models/gbe/gbe-error.model';
import { DataTransformerRequest, Transformer } from '@gfe/core/models/transformers.model';
import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { CsvTransformerService } from '@gfe/core/services/csv-transformer/csv-transformer.service';
import { occurrenceText } from '@gfe/core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@gfe/core/services/gbe-error-parser/gbe-error-parser.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { selectDataspaceState } from '@gfe/core/store/dataspace/dataspace.selectors';
import { convertKeysToSnakeCase } from '@gfe/core/utils/case-conversions';
import { numberRegex } from '@gfe/core/utils/json-schema-utils';
import { TimeValue } from '@gfe/shared/components/dropdown/template/timepicker-dropdown/timepicker-dropdown.component';
import {
  AvailableFilterField,
  FilterConfiguration,
  FilterDataType
} from '@gfe/shared/components/filter-query-builder/filter.models';
import {
  QualifierValue,
  QualifiersConfig,
  SearchResult
} from '@gfe/shared/components/search-query-builder/search-query-builder.component';
import { SearchResultBuilder } from '@gfe/shared/components/search-query-builder/search-result-builder';
import {
  formatTime,
  processTimeValue
} from '@gfe/shared/components/table/table-cell/utils';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxDropdownButton,
  ToolboxItem,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';
import { DropdownItem } from '@gfe/shared/models/dropdown-item.model';

import { FilterField } from '@core/filters/path-filter/vocabulary/vocabulary';
import { FieldPath, segmentName } from '@core/filters/path-filter/wire';
import {
  registrationSlotKey,
  registrationSlots
} from '@core/models/entities/implementations/user-entity-registrations';
import { ICONS } from '@core/services/icons/icons.service';
import { foldForSearch } from '@core/utils/search-text.utils';
import { dedup, isDefinedAndNotEmpty, isNil, isNilOrEmpty } from '@core/utils/shared-utils';

import { FilterQueryBuilderComponent } from '@shared/components/filter-query-builder/filter-query-builder.component';
import {
  ModalComponent,
  ModalConfig,
  ModalType
} from '@shared/components/modal/modal.component';
import { PathFilterComposerComponent } from '@shared/components/path-filter/composer/path-filter-composer.component';
import { PathFilterPanelComponent } from '@shared/components/path-filter/panel/path-filter-panel.component';
import { PathFilterPillsComponent } from '@shared/components/path-filter/pills/path-filter-pills.component';
import { PathFilterSearchBarComponent } from '@shared/components/path-filter/search-bar/path-filter-search-bar.component';
import {
  FormBuilderComponent,
  getValidators
} from '@shared/components/row-edition-form/form-builder/form-builder.component';
import {
  FormBuilderFieldType,
  FormBuilderSchema
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { ErrorsListComponent } from '@shared/components/table/errors-list/errors-list.component';
import { TableComponent } from '@shared/components/table/table.component';
import {
  CellOptions,
  RowEvent,
  SortState,
  TableColumn,
  TableConfig,
  TableRowError
} from '@shared/components/table/table.model';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { ReturnFocusDirective } from '@shared/directives/return-focus.directive';
import {
  addOrReplaceConditionInConfig,
  collectActiveFilterFields,
  createFilterCondition,
  findFilterField,
  removeConditionFromConfig,
  uidListFilter
} from '@shared/utils/filter-condition.utils';
import { makeFilterPredicate } from '@shared/utils/filter-evaluator.utils';
import { makePathFilterPredicate } from '@shared/utils/path-filter-evaluator.utils';

import { PathFilterBarState } from '@features/components/entities/path-filter-bar/path-filter-bar.state';
import { SummaryItem } from '@features/components/entities/summary/summary-item';

import { jobImportVocabulary } from './job-import-vocabulary';

export enum SiteKind {
  CLIENT = 'Client',
  WAREHOUSE = 'Warehouse',
  PROVIDER = 'Supplier',
  VEHICLE_BASE = 'VehicleBase'
}

export interface SemanticFlowKindUids {
  delivery: string; // Livraison
  reverseLogistics: string; // Reprise
  fromProvidersToWarehouses: string; // Ramasse
}

const DEFAULT_TRANSFORMER_INPUT: TransformerInput = {
  flowTypeToUidMap: {
    delivery: 'Livraison',
    reverseLogistics: 'Reprise',
    fromProvidersToWarehouses: 'Ramasse'
  },
  smallPalletCommodityUids: ['FRAIS'],
  smallPalletCapUid: 'PAL_SOL_DE',
  largePalletCapUid: 'PAL_SOL_SE',
  nbSecsPerPickupPallet: 65,
  nbSecsPerDeliveryPallet: 65,
  // Weight (kg) capacity requirement, derived from the carried commodity. Each pallet
  // weighs `waterKgPerPallet` for water, `freshKgPerPallet` for the small-pallet
  // commodities (see `smallPalletCommodityUids`), and `defaultKgPerPallet` otherwise,
  // unless the commodity carries a valid `kgPerPalletLabel` custom label.
  weightCapUid: 'POIDS_PAL',
  waterCommodityUid: 'EAU',
  waterKgPerPallet: 1000,
  freshKgPerPallet: 375,
  defaultKgPerPallet: 750,
  kgPerPalletLabel: 'Charge utile en Kg',
  deliveryDelegationLabel: 'real_delivery_site_uid',
  commodityAndJobKindColorOptions: [
    {
      commodityUid: 'PGC',
      jobKindUid: 'PP',
      hexColor: '#FEFF54'
    },
    {
      commodityUid: 'EAU',
      jobKindUid: 'PP',
      hexColor: '#FEFF54'
    },
    {
      commodityUid: 'FRAIS_AMBIANT',
      jobKindUid: 'PE',
      hexColor: '#6D9FEC'
    }
  ]
};

export interface CommodityAndJobKindColorOption {
  commodityUid: string;
  jobKindUid: string;
  hexColor: string;
}

export interface TransformerInput {
  // Map of the flow type (known behavior) to the uid in the database
  flowTypeToUidMap: SemanticFlowKindUids;
  smallPalletCommodityUids: string[];
  smallPalletCapUid: string;
  largePalletCapUid: string;
  nbSecsPerPickupPallet: number;
  nbSecsPerDeliveryPallet: number;
  // UID of the weight (kg) capacity measure required per pallet
  weightCapUid: string;
  // UID of the commodity representing water
  waterCommodityUid: string;
  // Weight (kg) of a single pallet of water
  waterKgPerPallet: number;
  // Weight (kg) of a single small pallet (see `smallPalletCommodityUids`)
  freshKgPerPallet: number;
  // Weight (kg) of a single pallet for any other commodity
  defaultKgPerPallet: number;
  // Key of the commodity custom label holding the weight (kg) of one of its pallets.
  // When a commodity's label value is a positive number, it overrides the three weights
  // above for that commodity.
  kgPerPalletLabel: string;
  // Key of the delivery schedule custom label naming the site the deliveries of that slot
  // are really made at. The slot's own site then becomes the ordering site.
  deliveryDelegationLabel: string;
  commodityAndJobKindColorOptions: CommodityAndJobKindColorOption[];
}

export interface FormData {
  // Absent when the day is unknown, which happens when a row is expanded before any
  // generation has run. Every consumer must then skip the day-dependent work rather than
  // build a Date out of nothing.
  date?: string;
  flow_types?: string[];
  job_kinds?: string[];
  commodities?: string[];
  pickup_site_uids?: string[];
  delivery_site_uids?: string[];
  time_begin?: string;
  time_end?: string;
}

export interface ExtendedSiteSchedule extends SiteSchedule {
  siteUid: string;
  siteKinds: string[];
  siteIsProvider: boolean;
}

export interface DecoratedSiteSchedules {
  schedules: ExtendedSiteSchedule[];
  siteKindsByUid: Record<string, string[]>;
}

// Tag every opening schedule with the site that declares it, and index the site kinds by
// uid. Both the generation pass and the expansion of a manually added row read schedules
// through here.
export function decorateSiteSchedules(sites: UserSiteV1Model[]): DecoratedSiteSchedules {
  const siteKindsByUid: Record<string, string[]> = {};
  const schedules = sites.flatMap((site) => {
    const siteKinds = site.kinds.map(siteKindToString);
    siteKindsByUid[site.uid] = siteKinds;
    const siteSchedules = (site.opening_schedules ?? []) as ExtendedSiteSchedule[];
    const hasProviderKind = siteKinds.includes(SiteKind.PROVIDER);
    siteSchedules.forEach((sch) => {
      sch.siteUid = site.uid;
      sch.siteKinds = siteKinds;
      sch.siteIsProvider = hasProviderKind;
    });

    return siteSchedules;
  });

  return { schedules, siteKindsByUid };
}

function buildFormSchema(
  commodities: string[],
  jobKinds: string[],
  flowKindNames: string[]
): FormBuilderSchema {
  const formSchema: FormBuilderSchema = {
    items: [
      {
        id: 'date',
        type: FormBuilderFieldType.DATE,
        needTime: false,
        isArray: false,
        fieldOrder: 1,
        label: {
          fr: 'Date du jour de livraison',
          en: 'Delivery day date',
          es: 'Fecha del día de entrega',
          'pt-BR': 'Data do dia de entrega'
        },
        required: true
      },
      {
        id: 'flow_types',
        type: FormBuilderFieldType.AUTO_COMPLETE,
        isArray: false,
        fieldOrder: 4,
        allowCustomValues: false,
        options: flowKindNames,
        label: {
          fr: 'Types de flux',
          en: 'Types of flow',
          es: 'Tipos de flujo',
          'pt-BR': 'Tipos de fluxo'
        },
        required: true
      },
      {
        id: 'pickup_site_uids',
        type: FormBuilderFieldType.ENTITY_AUTO_COMPLETE,
        isArray: true,
        entityType: 'SITE',
        fieldOrder: 2,
        label: {
          fr: 'Sites de chargement',
          en: 'Loading sites',
          es: 'Centros de carga',
          'pt-BR': 'Locais de carregamento'
        },
        required: true
      },
      {
        id: 'delivery_site_uids',
        type: FormBuilderFieldType.ENTITY_AUTO_COMPLETE,
        isArray: true,
        entityType: 'SITE',
        fieldOrder: 3,
        label: {
          fr: 'Sites de livraison',
          en: 'Unloading sites',
          es: 'Centros de entrega',
          'pt-BR': 'Locais de entrega'
        },
        required: false
      },
      {
        id: 'job_kinds',
        type: FormBuilderFieldType.AUTO_COMPLETE,
        isArray: false,
        fieldOrder: 5,
        allowCustomValues: false,
        options: jobKinds,
        label: {
          fr: 'Types de commande',
          en: 'Order types',
          es: 'Tipos de pedido',
          'pt-BR': 'Tipos de pedido'
        },
        required: false
      },
      {
        id: 'commodities',
        type: FormBuilderFieldType.AUTO_COMPLETE,
        isArray: false,
        fieldOrder: 6,
        allowCustomValues: false,
        options: commodities,
        label: {
          fr: 'Types de marchandise',
          en: 'Commodity types',
          es: 'Tipos de material',
          'pt-BR': 'Tipos de mercadoria'
        },
        required: false
      },
      {
        id: 'time_begin',
        type: FormBuilderFieldType.TIME,
        isArray: false,
        fieldOrder: 7,
        label: {
          fr: 'Plage horaire début',
          en: 'Time slot start',
          es: 'Inicio de la franja horaria',
          'pt-BR': 'Início da janela de horário'
        },
        required: false
      },
      {
        id: 'time_end',
        type: FormBuilderFieldType.TIME,
        isArray: false,
        fieldOrder: 8,
        label: {
          fr: 'Plage horaire fin',
          en: 'Time slot end',
          es: 'Fin de la franja horaria',
          'pt-BR': 'Fim da janela de horário'
        },
        required: false
      }
    ]
  };
  return formSchema;
}

function createFormGroup(
  fb: FormBuilder,
  commodities: string[],
  jobKinds: string[],
  flowKindNames: string[]
): FormGroup {
  const group: { [key: string]: FormControl | FormGroup | FormArray } = {};

  for (const item of buildFormSchema(commodities, jobKinds, flowKindNames).items) {
    if (!('type' in item)) {
      continue;
    }

    const validators = getValidators(item);
    group[item.id] = new FormControl(null, validators);
  }

  return fb.group(group);
}

export interface TableRow {
  uid: string;
  pickup_site_uid: string;
  pickup_window_start: TimeValue;
  pickup_window_finish: TimeValue;
  // Softness (earliness/lateness) inherited from the pickup site schedule for which
  // the row was generated. Replicated verbatim onto the generated pickup operation
  // window.
  pickup_earliness_cfg?: EarlinessCfg | null;
  pickup_lateness_cfg?: LatenessCfg | null;
  delivery_site_uid: string;
  delivery_window_start: TimeValue;
  delivery_window_finish: TimeValue;
  // Softness (earliness/lateness) inherited from the delivery site schedule for
  // which the row was generated.
  delivery_earliness_cfg?: EarlinessCfg | null;
  delivery_lateness_cfg?: LatenessCfg | null;
  // Where the goods were prepared and which site ordered them, when those differ from the
  // pickup and delivery sites. Descriptive only: the operations always use the pickup and
  // delivery sites.
  preparing_site_uid?: string | null;
  ordering_site_uid?: string | null;
  flow_type?: string;
  commodity: string;
  nb_pallets: Record<string, string>;
  assigned_transporter_uid: string | null;
  assigned_vehicle_uid: string | null;
  assigned_vehicle_registration: string | null;
  // A row the user added by hand that the generation has not filled in yet. Deliberately
  // camelCase: it is grid state, not part of the uploaded transport order. Only
  // `createDefaultTableRow` sets it, so a generated row is never expanded when the user
  // edits its flow type or loading site.
  isManualDraft?: boolean;
}

// Window a manually added row carries until the generation fills it from the site
// schedules, and the fallback it keeps when the site declares nothing coherent.
const DEFAULT_ROW_WINDOW_START: TimeValue = { hours: 8, minutes: 0 };
const DEFAULT_ROW_WINDOW_FINISH: TimeValue = { hours: 18, minutes: 0 };

function isSameTimeValue(a: TimeValue, b: TimeValue): boolean {
  return a.hours === b.hours && a.minutes === b.minutes;
}

// A window the user has not touched since the row was added. Both bounds are considered
// together: they are entered as a pair, and a window is only meaningful as one.
function isDefaultWindow(start: TimeValue, finish: TimeValue): boolean {
  return (
    isSameTimeValue(start, DEFAULT_ROW_WINDOW_START) &&
    isSameTimeValue(finish, DEFAULT_ROW_WINDOW_FINISH)
  );
}

// Column key of the per-row quantity total. Deliberately not a `TableRow` field: the value
// is derived from `nb_pallets` by the column's renderHook, never stored.
export const TOTAL_COLUMN_KEY = 'nb_pallets_total' as keyof TableRow;

// Quantities entered on a row, totalled exactly as the import totals them: whatever
// `makeQuantVec` drops (blanks, zeroes, unparsable entries) does not count here either.
export function rowPalletTotal(row: TableRow): number {
  return makeQuantVec(row).reduce((total, quant) => total + quant.quantity, 0);
}

/**
 * A column the summary footer can aggregate. `summaryTypes` is what `SummaryItem` reads to
 * know which statistics a column carries; `TableColumn` itself does not declare it.
 */
export type SummarisedColumn = TableColumn<TableRow> & {
  summaryTypes?: EntitySummaryType[];
};

// One quantity column per order type, followed by their per-row total. With a single order
// type the total would restate that column value for value, so it is only worth a column
// beyond that. Each of them feeds a subtotal in the summary footer.
export function buildQuantityColumns(jobKindUids: string[]): SummarisedColumn[] {
  const columns: SummarisedColumn[] = jobKindUids.map((uid) => ({
    key: `nb_pallets.${uid}` as keyof TableRow,
    label: uid,
    color: '#93CAE8',
    width: 80,
    config: { sortable: true, resizeable: true },
    summaryTypes: ['sum'],
    // Without this the subtotal would read the cell through `Number()`, which counts
    // entries the import ignores and misses the comma decimals it accepts — the footer
    // would then not add up to its own Total.
    rawValue: (row?: TableRow) =>
      isNil(row) ? undefined : palletQuantity(row.nb_pallets[uid])
  }));

  if (jobKindUids.length > 1) {
    columns.push({
      key: TOTAL_COLUMN_KEY,
      label: 'Total',
      color: '#93CAE8',
      width: 80,
      readonly: true,
      config: { sortable: false },
      summaryTypes: ['sum'],
      renderHook: (_value: unknown, row?: TableRow) =>
        isNil(row) ? undefined : rowPalletTotal(row)
    });
  }

  return columns;
}

const PALLET_FIELD_PREFIX = 'nb_pallets.';

/**
 * The fields the filter builder offers, derived from the grid's own columns so a column added
 * to the grid is filterable under the type it is edited with. Time columns are offered as
 * durations since midnight: the builder has no time-of-day type.
 */
export function buildAdvancedFilterFields(
  columns: SummarisedColumn[]
): AvailableFilterField[] {
  return columns.flatMap((column): AvailableFilterField[] => {
    const field = String(column.key);
    const label = column.label;

    if (field.startsWith(PALLET_FIELD_PREFIX)) {
      return [{ field, label, dataType: FilterDataType.NUMBER }];
    }
    switch (column.type) {
      case 'uid':
        return [
          {
            field,
            label,
            dataType: FilterDataType.UID,
            entityType: column.editorConfig?.uidEntityType
          }
        ];
      case 'time':
        return [{ field, label, dataType: FilterDataType.DURATION }];
      default:
        return field === 'assigned_vehicle_registration'
          ? [{ field, label, dataType: FilterDataType.TEXT }]
          : [];
    }
  });
}

export interface RegistrationPrefixes {
  copy: string;
  code: string;
}

export function registrationLabel(
  value: string | null | undefined,
  prefixes: RegistrationPrefixes
): string {
  if (!value) return '';
  if (value.startsWith('copy:')) return `${prefixes.copy} ${value.slice(5)}`;
  if (value.startsWith('code:')) return `${prefixes.code} ${value.slice(5)}`;
  return value;
}

/**
 * The value the filter builder compares for a cell. Quantities go through `palletQuantity`,
 * like the footer subtotals, so a filter never disagrees with the subtotal about a cell; a
 * cell carrying nothing reads as 0 so that `< 3` keeps it. The registration is compared as
 * displayed, since the stored `copy:` / `code:` form is never shown.
 */
export function advancedFilterValue(
  row: TableRow,
  field: string,
  registrationPrefixes: RegistrationPrefixes
): unknown {
  if (field.startsWith(PALLET_FIELD_PREFIX)) {
    return palletQuantity(row.nb_pallets[field.slice(PALLET_FIELD_PREFIX.length)]) ?? 0;
  }
  if (field === 'assigned_vehicle_registration') {
    return registrationLabel(row.assigned_vehicle_registration, registrationPrefixes);
  }
  const value: unknown = row[field as keyof TableRow];
  if (isTimeValue(value)) {
    return value.hours * 60 + value.minutes;
  }
  return value;
}

function isTimeValue(value: unknown): value is TimeValue {
  return (
    typeof value === 'object' && !isNil(value) && 'hours' in value && 'minutes' in value
  );
}

const MS_PER_MINUTE = 60_000;

function hasSearchTerms(result: SearchResult | null): result is SearchResult {
  return (
    !isNil(result) &&
    (Object.keys(result.qualifiers).length > 0 || result.textTerms.length > 0)
  );
}

/**
 * The value the Path evaluator compares for a cell: `advancedFilterValue`'s, except that a time
 * of day is in milliseconds since midnight, the unit Path duration literals are compared in.
 */
export function pathFilterValue(
  row: TableRow,
  path: FieldPath,
  registrationPrefixes: RegistrationPrefixes
): unknown {
  const field = path.segments.map(segmentName).join('.');
  const value = advancedFilterValue(row, field, registrationPrefixes);
  return isTimeValue(row[field as keyof TableRow])
    ? (value as number) * MS_PER_MINUTE
    : value;
}

interface StoredComponentState {
  rows: TableRow[];
  formValue: Record<string, unknown>;
  isFormExpanded: boolean;
  selectedDateISO: string | undefined;
  cellOptions: CellOptions<TableRow>[];
  siteKindsByUid: Record<string, string[]>;
}

@Component({
  selector: 'app-job-helped-importation',
  imports: [
    ErrorsListComponent,
    TableComponent,
    ReactiveFormsModule,
    FormBuilderComponent,
    NgxSkeletonLoaderComponent,
    TranslatePipe,
    ToolboxComponent,
    ModalComponent,
    FilterQueryBuilderComponent,

    PathFilterSearchBarComponent,
    PathFilterPillsComponent,
    PathFilterPanelComponent,
    PathFilterComposerComponent,
    FocusFirstDirective,
    ReturnFocusDirective
  ],
  providers: [PathFilterBarState],
  templateUrl: './job-helped-importation.component.html'
})
export class JobHelpedImportationComponent {
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly selector = inject(SelectorsService);
  private readonly currentLangService = inject(CurrentLangService);
  private readonly csvTransformersService = inject(CsvTransformerService);
  private readonly store = inject(Store);
  private readonly gbeErrorParserService = inject(GbeErrorParserService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  // Shared, cached read of every site's opening schedules. A plain field rather than a
  // signal: nothing in the template or in a computed reads it.
  private allSchedules?: Observable<ExtendedSiteSchedule[]>;

  private dataspace = this.store.selectSignal(selectDataspaceState);
  protected readonly ICONS = ICONS;

  protected isUploading = signal<boolean>(false);

  protected confirmModal = signal<{
    isOpen: boolean;
    titleKey: string;
    descriptionKey: string;
    config: ModalConfig;
  }>({
    isOpen: false,
    titleKey: '',
    descriptionKey: '',
    config: {
      icon: 'warning',
      type: ModalType.ERROR,
      askingModal: true,
      dismissible: true,
      closeOnBackdropClick: true,
      actions: []
    }
  });

  protected closeConfirmModal(): void {
    this.confirmModal.update((m) => ({ ...m, isOpen: false }));
  }

  private openConfirmModal(
    titleKey: string,
    descriptionKey: string,
    onConfirm: () => void
  ): void {
    this.confirmModal.update(() => ({
      isOpen: true,
      titleKey,
      descriptionKey,
      config: {
        icon: 'warning',
        type: ModalType.ERROR,
        askingModal: true,
        dismissible: true,
        closeOnBackdropClick: true,
        actions: [
          {
            label: this.translate.instant('common.cancel'),
            color: 'cancel',
            callback: () => {
              this.confirmModal.update((m) => ({ ...m, isOpen: false }));
            }
          },
          {
            label: this.translate.instant('common.submit'),
            color: 'danger',
            callback: () => {
              this.confirmModal.update((m) => ({ ...m, isOpen: false }));
              onConfirm();
            }
          }
        ]
      }
    }));
  }

  private readonly tranformerId = 'scarmor';
  private readonly entityType = 'JOB';

  private selectedDate = signal<Date | undefined>(undefined);

  protected selectedDateInLocale = computed<string | undefined>(() => {
    const date = this.selectedDate();
    if (isNil(date)) {
      return undefined;
    }
    return date.toLocaleDateString(this.currentLangService.currentLang(), {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  });

  protected transformerInput = toSignal<TransformerInput | undefined>(
    toObservable(this.dataspace).pipe(
      switchMap(({ dataspace }) => {
        if (isNil(dataspace)) {
          return of(undefined);
        }
        return this.csvTransformersService
          .getCustomTransformers(dataspace.id, this.entityType)
          .pipe(
            map((response) => {
              const transformer = response.find((t) => t.id === this.tranformerId);
              return {
                ...DEFAULT_TRANSFORMER_INPUT,
                ...((transformer?.data ?? {}) as TransformerInput)
              };
            })
          );
      })
    ),
    { initialValue: undefined }
  );

  protected allFlowKinds = toSignal(
    toObservable(this.dataspace).pipe(
      switchMap(({ dataspace }) => {
        if (isNil(dataspace)) {
          return of([]);
        }
        const request: EntitySelectorRequestModel = {
          entityType: 'flow_kind',
          dataSpaceId: dataspace.id,
          entityTypeVersion: new UserFlowKindCfg().versionName()
        };

        return this.selector
          .selectAllData$<UserFlowKindV1Model>(request)
          .pipe(map((res) => res.items.filter((item) => !isNil(item.name))));
      })
    ),
    { initialValue: [] as UserFlowKindV1Model[] }
  );

  protected allVehicles = toSignal(
    toObservable(this.dataspace).pipe(
      switchMap(({ dataspace }) => {
        if (isNil(dataspace)) {
          return of([]);
        }
        const request: EntitySelectorRequestModel = {
          entityType: 'vehicle',
          dataSpaceId: dataspace.id,
          entityTypeVersion: new UserVehicleCfg().versionName()
        };
        return this.selector
          .selectAllData$<UserVehicleV1Model>(request)
          .pipe(map((res) => res.items));
      })
    ),
    { initialValue: [] as UserVehicleV1Model[] }
  );

  private vehicleRegistrationOptions = computed<
    Record<string, { values: string[]; labels: string[] }>
  >(() => {
    this.currentLangService.currentLang();
    const copyPrefix = this.translate.instant('entities.vehicle_registration.copy');
    const codePrefix = this.translate.instant('entities.vehicle_registration.code');
    const map: Record<string, { values: string[]; labels: string[] }> = {};
    for (const v of this.allVehicles()) {
      const slots = registrationSlots(v.registrations);
      map[v.uid] = {
        values: slots.map((slot) => registrationSlotKey(slot)),
        labels: slots.map((slot) =>
          'copy' in slot ? `${copyPrefix} ${slot.copy}` : `${codePrefix} ${slot.code}`
        )
      };
    }
    return map;
  });

  private allFlowKindNames = computed<string[]>(() => {
    return this.allFlowKinds().map((flowKind) => flowKind.name);
  });

  private flowKindNameToUidMap = computed<Map<string, string>>(() => {
    const flowKinds = this.allFlowKinds();
    return new Map(flowKinds.map((fk) => [fk.name, fk.uid]));
  });

  private flowKindUidToNameMap = computed<Record<string, string>>(() => {
    const flowKinds = this.allFlowKinds();
    return Object.fromEntries(flowKinds.map((fk) => [fk.uid, fk.name]));
  });

  protected allJobKinds = toSignal(
    toObservable(this.dataspace).pipe(
      switchMap(({ dataspace }) => {
        if (isNil(dataspace)) {
          return of([]);
        }
        const request: EntitySelectorRequestModel = {
          entityType: 'job_kind',
          dataSpaceId: dataspace.id,
          entityTypeVersion: new UserJobKindCfg().versionName()
        };

        return this.selector
          .selectAllData$<UserJobKindV1Model>(request)
          .pipe(map((res) => res.items.filter((item) => !isNil(item.name))));
      })
    ),
    { initialValue: [] as UserJobKindV1Model[] }
  );

  private allJobKindNames = computed<string[]>(() => {
    return this.allJobKinds().map((jobKind) => jobKind.name);
  });

  private allJobKindUids = computed<string[]>(() => {
    return this.allJobKinds().map((jobKind) => jobKind.uid);
  });

  private jobKindNameToUidMap = computed<Map<string, string>>(() => {
    const jobKinds = this.allJobKinds();
    return new Map(jobKinds.map((jk) => [jk.name, jk.uid]));
  });

  protected allCommodities = toSignal(
    toObservable(this.dataspace).pipe(
      switchMap(({ dataspace }) => {
        if (isNil(dataspace)) {
          return of([]);
        }
        const request: EntitySelectorRequestModel = {
          entityType: 'commodity',
          dataSpaceId: dataspace.id,
          entityTypeVersion: new UserCommodityCfg().versionName()
        };

        return this.selector
          .selectAllData$<UserCommodityV1Model>(request)
          .pipe(map((res) => res.items.filter((item) => !isNil(item.name))));
      })
    ),
    { initialValue: [] as UserCommodityV1Model[] }
  );

  private allCommodityNames = computed<string[]>(() => {
    return this.allCommodities().map((commodity) => commodity.name);
  });
  private allCommodityUids = computed<string[]>(() => {
    return this.allCommodities().map((commodity) => commodity.uid);
  });
  private commodityNameToUidMap = computed<Map<string, string>>(() => {
    const commodities = this.allCommodities();
    return new Map(commodities.map((c) => [c.name, c.uid]));
  });

  private commodityUidToNameMap = computed<Record<string, string>>(() => {
    const commodities = this.allCommodities();
    return Object.fromEntries(commodities.map((c) => [c.uid, c.name]));
  });

  private kgPerPalletByCommodity = computed<Record<string, number>>(() => {
    const input = this.transformerInput();
    if (isNil(input)) {
      return {};
    }
    return buildKgPerPalletByCommodity(this.allCommodities(), input.kgPerPalletLabel);
  });

  @ViewChild(ToolboxComponent)
  toolboxComponent!: ToolboxComponent;

  private readonly injector = inject(Injector);
  protected readonly pathBar = inject(PathFilterBarState);
  protected readonly activeGrammar = this.pathBar.activeGrammar;
  private readonly pills = viewChild(PathFilterPillsComponent);

  tableConfig: TableConfig<TableRow> = {
    rowId: 'uid',
    selection: {
      enabled: true,
      multiple: true,
      mode: 'checkbox',
      highlightSelected: true,
      // A closure rather than a bound method: `tableConfig` is initialised before the class
      // fields it would otherwise capture.
      action: (row: TableRow) => this.toggleRowSelection(row)
    },
    bordered: true,
    columnColors: true,
    striped: true,
    // Banded by client rather than row by row: the rows serving one client are contiguous
    // (`defaultSortingFunction` groups on this very key), so one tint per client makes the
    // runs readable at a glance. Also a closure, for the reason above.
    stripeGroupKey: (row: TableRow) => clientSiteUid(row, this.siteKindsByUid()),
    stripeColor: 'var(--row-stripe-success)',
    editable: {
      inline: true,
      newTab: false
    },
    horizontalScroll: true,
    errors: true,
    columnTooltips: true,
    rowNumbers: true,
    columnActionSettings: {
      movable: true,
      resizeable: true,
      sortable: true,
      filterable: true,
      groupable: false,
      hideable: false
    }
  };

  searchCfg = computed<SearchConfig>(() => {
    return {
      active: true,
      type: 'advanced',
      shortcut: true,
      qualifiers: {
        site_collecte: {
          type: 'text',
          description: 'Site de collecte',
          supportsRegex: false
        },
        site_livraison: {
          type: 'text',
          description: 'Site de livraison',
          supportsRegex: false
        },
        site_preparation: {
          type: 'text',
          description: 'Site de préparation',
          supportsRegex: false
        },
        site_commande: {
          type: 'text',
          description: 'Site de commande',
          supportsRegex: false
        },
        flux: {
          type: 'enum',
          description: 'Type de Flux',
          values: this.allFlowKindNames(),
          supportsRegex: false
        },
        marchandise: {
          type: 'enum',
          description: 'Type de marchandise',
          values: this.allCommodityNames(),
          supportsRegex: false
        },
        heure_debut_collecte: {
          type: 'text',
          description: 'Heure début collecte (HH:MM)',
          supportsRegex: false
        },
        heure_fin_collecte: {
          type: 'text',
          description: 'Heure fin collecte (HH:MM)',
          supportsRegex: false
        },
        heure_debut_livraison: {
          type: 'text',
          description: 'Heure début livraison (HH:MM)',
          supportsRegex: false
        },
        heure_fin_livraison: {
          type: 'text',
          description: 'Heure fin livraison (HH:MM)',
          supportsRegex: false
        },
        transporteur: {
          type: 'text',
          description: 'Transporteur',
          supportsRegex: false
        },
        vehicule: {
          type: 'text',
          description: 'Véhicule',
          supportsRegex: false
        },
        palettes: {
          type: 'text',
          description: 'Palettes (type ou quantité)',
          supportsRegex: false
        }
      } as QualifiersConfig
    };
  });

  protected readonly toolboxSearchConfig = this.pathBar.toolboxSearch(() =>
    this.searchCfg()
  );

  toolboxSections = computed<ToolboxSection[]>(() => {
    this.currentLangService.currentLang();

    const sections: ToolboxSection[] = [
      {
        id: 'actions',
        items: [
          {
            id: 'reset',
            label: this.translate.instant('features.data.upload.toolbox.reset'),
            callback: () => {
              this.openConfirmModal(
                'job_assisted_importation.confirm_reset.title',
                'job_assisted_importation.confirm_reset.description',
                () => this.executeReset()
              );
            }
          },
          {
            id: 'import',
            icon: ICONS.upload,
            callback: () => {
              this.openConfirmModal(
                'job_assisted_importation.confirm_import.title',
                'job_assisted_importation.confirm_import.description',
                () => this.handleDataUploadWithTransformer()
              );
            },
            disabled: this.rows().length === 0,
            label: this.translate.instant('features.data.upload.toolbox.upload'),
            callToAction: true
          }
        ]
      },
      {
        id: 'bulk-actions',
        items: this.buildBulkActionItems()
      }
    ];

    return sections;
  });

  private buildBulkActionItems(): ToolboxItem[] {
    const hasSelection = this.selectedRows().length > 0;
    const key = (name: string) =>
      this.translate.instant(`job_assisted_importation.bulk_actions.${name}`);

    return [
      {
        id: 'duplicate-rows',
        icon: ICONS.copy,
        label: key('duplicate'),
        tooltip: key('duplicate_tooltip'),
        disabled: !hasSelection,
        callback: () => this.duplicateSelectedRows()
      },
      {
        id: 'delete-rows',
        icon: ICONS.trash,
        label: key('delete'),
        tooltip: key('delete_tooltip'),
        disabled: !hasSelection,
        callback: () => this.deleteSelectedRows()
      },
      {
        kind: 'entity-ref',
        id: 'assign-transporter',
        icon: ICONS.users,
        label: key('assign_transporter'),
        tooltip: key('assign_transporter_tooltip'),
        disabled: !hasSelection,
        entityType: 'company',
        onPick: (uid) => this.assignTransporter(uid),
        onClear: () => this.assignTransporter(null)
      },
      {
        kind: 'entity-ref',
        id: 'assign-vehicle',
        icon: ICONS.truck,
        label: key('assign_vehicle'),
        tooltip: key('assign_vehicle_tooltip'),
        disabled: !hasSelection,
        entityType: 'vehicle',
        onPick: (uid) => this.assignVehicle(uid),
        onClear: () => this.assignVehicle(null)
      },
      this.buildRegistrationDropdown(hasSelection, key)
    ];
  }

  private assignTransporter(uid: string | null): void {
    this.assignToSelectedRows((row) => {
      row.assigned_transporter_uid = uid;
    });
  }

  /**
   * The registration slots belong to the vehicle, so a kept slot would not exist on the new
   * one — and no slot at all exists once the vehicle is unset. Mirrors what an inline
   * vehicle edit already does.
   */
  private assignVehicle(uid: string | null): void {
    this.assignToSelectedRows((row) => {
      row.assigned_vehicle_uid = uid;
      row.assigned_vehicle_registration = null;
    });
  }

  /**
   * A "no value" entry followed by one entry per option, each writing the whole selection. The
   * check mark only appears where the selection already agrees on that value.
   */
  private buildAssignmentItems(
    key: keyof TableRow,
    options: { value: string; label: string }[],
    apply: (row: TableRow, value: string | null) => void
  ): DropdownItem[] {
    const { agreed, value: shared } = this.sharedValueAcrossSelection(key);
    const clearLabel = this.translate.instant(
      'job_assisted_importation.bulk_actions.clear_value'
    );

    return [
      {
        label: clearLabel,
        isSelected: agreed && isNil(shared),
        action: () => this.assignToSelectedRows((row) => apply(row, null))
      },
      { isSeparator: true },
      ...options.map((option) => ({
        label: option.label,
        isSelected: agreed && option.value === shared,
        action: () => this.assignToSelectedRows((row) => apply(row, option.value))
      }))
    ];
  }

  /**
   * Registration slots (`copy:N` / `code:S`) are defined by one vehicle's own registrations, so
   * the list is only meaningful once the selection agrees on a vehicle type. Offering the union
   * instead would let a slot be written onto a row whose vehicle does not have it, and the
   * failure would only surface at import.
   */
  private buildRegistrationDropdown(
    hasSelection: boolean,
    key: (name: string) => string
  ): ToolboxDropdownButton {
    const sharedVehicleUid = this.sharedValueAcrossSelection('assigned_vehicle_uid').value;
    const options = isNil(sharedVehicleUid)
      ? undefined
      : this.vehicleRegistrationOptions()[sharedVehicleUid];

    return {
      id: 'assign-registration',
      icon: ICONS.identificationCard,
      label: key('assign_registration'),
      tooltip:
        isNil(options) && hasSelection
          ? key('mixed_vehicles')
          : key('assign_registration_tooltip'),
      disabled: !hasSelection || isNil(options),
      items: this.buildAssignmentItems(
        'assigned_vehicle_registration',
        (options?.values ?? []).map((value, index) => ({
          value,
          label: options?.labels[index] ?? value
        })),
        (row, value) => {
          row.assigned_vehicle_registration = value;
        }
      )
    };
  }

  protected schema = computed<FormBuilderSchema>(() => {
    return buildFormSchema(
      this.allCommodityNames(),
      this.allJobKindNames(),
      this.allFlowKindNames()
    );
  });
  protected form: FormGroup = createFormGroup(this.fb, [], [], []);
  protected rows = signal<TableRow[]>([]);
  protected siteKindsByUid = signal<Record<string, string[]>>({});
  protected userSortState = signal<SortState<TableRow>[]>([]);
  /** Set to a fresh empty list to clear the table's own header sort. */
  protected tableInitialSort = signal<SortState<TableRow>[]>([]);
  protected searchTerm = signal<string>('');
  protected searchResult = signal<SearchResult | null>(null);
  protected advancedFilters = signal<FilterConfiguration | null>(null);
  protected showAdvancedFiltering = signal<boolean>(false);
  protected openFilterForField = signal<string | null>(null);
  private registrationPrefixes = computed<RegistrationPrefixes>(() => {
    this.currentLangService.currentLang();
    return {
      copy: this.translate.instant('entities.vehicle_registration.copy'),
      code: this.translate.instant('entities.vehicle_registration.code')
    };
  });
  protected availableAdvancedFilters = computed<AvailableFilterField[]>(() =>
    buildAdvancedFilterFields(this.columns())
  );
  protected cellOptions = signal<CellOptions<TableRow>[]>([]);

  // Selection is held by uid rather than by row object: `loadStateFromStorage` rebuilds the
  // rows from JSON and `expandManualRow` splices new ones in, so a reference-keyed set would
  // go stale. Transient by design — not part of `StoredComponentState`.
  protected selectedRowUids = signal<Set<string>>(new Set<string>());

  // Resolved against the current rows, so a uid left behind by a reset or an expansion simply
  // stops counting instead of aiming a bulk action at a row the grid no longer holds.
  protected selectedRows = computed<TableRow[]>(() => {
    const selected = this.selectedRowUids();
    return this.rows().filter((row) => selected.has(row.uid));
  });

  // What `app-table` compares against; it matches by row reference.
  protected selectedRowsSet = computed<Set<TableRow>>(() => new Set(this.selectedRows()));

  protected toggleRowSelection(row: TableRow): void {
    this.selectedRowUids.update((selected) => {
      const next = new Set(selected);
      if (!next.delete(row.uid)) {
        next.add(row.uid);
      }
      return next;
    });
  }

  protected columns = computed<SummarisedColumn[]>(() => {
    this.currentLangService.currentLang();
    const jobKindUids = this.allJobKindUids();
    const flowKindUidToNameMap = this.flowKindUidToNameMap();
    const commodityUidToNameMap = this.commodityUidToNameMap();
    const vehicleRegistrationOptions = this.vehicleRegistrationOptions();
    const registrationPrefixes = this.registrationPrefixes();

    const baseColumns: TableColumn<TableRow>[] = [
      {
        key: 'pickup_site_uid',
        label: 'Site de chargement',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'site'
        }
      },
      {
        key: 'preparing_site_uid',
        label: 'Site de préparation',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'site'
        }
      },
      {
        key: 'pickup_window_start',
        label: 'Début de plage de chargement',
        type: 'time',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'pickup_window_finish',
        label: 'Fin de plage de chargement',
        type: 'time',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'delivery_site_uid',
        label: 'Site de livraison',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'site'
        }
      },
      {
        key: 'ordering_site_uid',
        label: 'Site de commande',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'site'
        }
      },
      {
        key: 'delivery_window_start',
        label: 'Début de plage de livraison',
        type: 'time',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'delivery_window_finish',
        label: 'Fin de plage de livraison',
        type: 'time',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'flow_type',
        label: 'Type de flux',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'flow_kind'
        },
        renderHook: (value: unknown) =>
          flowKindUidToNameMap[value as string] ?? (value as string)
      },
      {
        key: 'commodity',
        label: 'Type de marchandise',
        type: 'uid',
        color: '#9FD77F',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true },
        editorConfig: {
          uidEntityType: 'commodity'
        },
        renderHook: (value: unknown) =>
          commodityUidToNameMap[value as string] ?? (value as string)
      }
    ];

    const suffixColumns: TableColumn<TableRow>[] = [
      {
        key: 'assigned_transporter_uid',
        label: 'Transporteur',
        type: 'uid',
        editorConfig: {
          uidEntityType: 'company'
        },
        color: '#EFC9B0',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'assigned_vehicle_uid',
        label: 'Type de véhicule',
        type: 'uid',
        editorConfig: {
          uidEntityType: 'vehicle'
        },
        color: '#EFC9B0',
        width: 100,
        config: { sortable: true, filterable: true, resizeable: true }
      },
      {
        key: 'assigned_vehicle_registration',
        label: 'Immatriculation / Copie',
        type: 'enum',
        editorConfigForRow: (row: TableRow) => {
          const vehicleUid = row.assigned_vehicle_uid;
          if (!vehicleUid) return undefined;
          const opts = vehicleRegistrationOptions[vehicleUid];
          return opts ? { enumValues: opts.values, enumLabels: opts.labels } : undefined;
        },
        renderHook: (value: unknown) =>
          registrationLabel(value as string | null, registrationPrefixes),
        color: '#EFC9B0',
        width: 100
      }
    ];

    return [...baseColumns, ...buildQuantityColumns(jobKindUids), ...suffixColumns];
  });

  // Kept apart from `columns` so a filter change does not rebuild the builder's field list.
  protected tableColumns = computed<SummarisedColumn[]>(() => {
    const activeFields =
      this.activeGrammar() === 'path'
        ? this.pathBar.filteredColumnNames()
        : collectActiveFilterFields(this.advancedFilters());
    return this.columns().map((column) => ({
      ...column,
      filtered: activeFields.has(String(column.key))
    }));
  });

  protected sortedRows = computed<TableRow[]>(() => {
    const rows = this.rows();
    const userSort = this.userSortState();
    const siteKindsByUid = this.siteKindsByUid();

    if (userSort.length > 0) {
      return [...rows].sort((a, b) => {
        for (const sort of userSort) {
          const aVal = a[sort.column!];
          const bVal = b[sort.column!];

          if (isNil(aVal) && isNil(bVal)) continue;
          if (isNil(aVal)) return 1;
          if (isNil(bVal)) return -1;

          let comparison: number;
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.localeCompare(bVal);
          } else if (typeof aVal === 'object' && 'hours' in aVal && 'minutes' in aVal) {
            const aMinutes = (aVal as TimeValue).hours * 60 + (aVal as TimeValue).minutes;
            const bMinutes = (bVal as TimeValue).hours * 60 + (bVal as TimeValue).minutes;
            comparison = aMinutes - bMinutes;
          } else {
            comparison = String(aVal).localeCompare(String(bVal));
          }

          if (comparison !== 0) {
            return sort.direction === 'asc' ? comparison : -comparison;
          }
        }

        return defaultSortingFunction(a, b, siteKindsByUid);
      });
    }

    return [...rows].sort((a, b) => defaultSortingFunction(a, b, siteKindsByUid));
  });

  protected filteredAndSortedRows = computed<TableRow[]>(() => {
    const sorted = this.sortedRows();
    const prefixes = this.registrationPrefixes();
    const path = this.activeGrammar() === 'path';
    const result = path
      ? { ...SearchResultBuilder.empty(), textTerms: this.pathBar.bar().search }
      : this.searchResult();
    const matchesFilters = path
      ? makePathFilterPredicate(this.pathBar.filter(), (row: TableRow, at) =>
          pathFilterValue(row, at, prefixes)
        )
      : makeFilterPredicate(this.advancedFilters(), (row: TableRow, field) =>
          advancedFilterValue(row, field, prefixes)
        );

    const searched = hasSearchTerms(result)
      ? this.applySearchFiltering(sorted, result)
      : sorted;
    return searched.filter(matchesFilters);
  });

  /**
   * Subtotals under the grid, over the rows it currently shows rather than every generated
   * row: a footer summing lines hidden by the search would contradict what is on screen.
   */
  protected summaryItems = computed<SummaryItem<TableRow>[]>(() => {
    const rows = this.filteredAndSortedRows();

    return this.columns()
      .filter((column) => !isNilOrEmpty(column.summaryTypes))
      .map((column) => {
        // SummaryItem is typed for the entity grids, whose rows are EntityInterface
        // wrappers. It only ever reads a column's key path, rawValue and renderHook, which
        // these plain rows answer just as well; the generic is the sole mismatch.
        const item = new SummaryItem<TableRow>(
          column as unknown as TableColCfg<EntityInterface<TableRow>>
        );
        item.addValues(rows as unknown as EntityInterface<TableRow>[]);
        return item;
      });
  });

  protected errors = signal<TableRowError<TableRow>[]>([]);
  protected currentIndexMap = signal<number[]>([]);
  protected isFormExpanded = signal<boolean>(true);

  private formStatusChanges = toSignal(this.form.statusChanges, {
    initialValue: this.form.status
  });

  private formValues = toSignal(this.form.valueChanges, { initialValue: this.form.value });

  protected isFormValid = computed<boolean>(() => {
    this.formStatusChanges();
    return this.form.valid;
  });

  constructor() {
    effect(() =>
      this.pathBar.ownVocabulary.set(jobImportVocabulary(this.availableAdvancedFilters()))
    );

    effect(() => {
      this.rows();
      this.siteKindsByUid();
      this.isFormExpanded();
      this.cellOptions();
      this.formValues();
      this.saveStateToStorage();
    });

    this.loadStateFromStorage();
  }

  private getStorageKey(): string {
    const dataspaceId = this.dataspace().dataspace?.id;
    const genName = this.activatedRoute.snapshot.params['genName'];
    return `job-helped-importation:${dataspaceId}:${genName}`;
  }

  private saveStateToStorage(): void {
    const state: StoredComponentState = {
      rows: this.rows(),
      formValue: this.form.value,
      isFormExpanded: this.isFormExpanded(),
      selectedDateISO: this.selectedDate()?.toISOString(),
      cellOptions: this.cellOptions(),
      siteKindsByUid: this.siteKindsByUid()
    };
    localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
  }

  private loadStateFromStorage(): void {
    const raw = localStorage.getItem(this.getStorageKey());
    if (!raw) return;
    try {
      const state: StoredComponentState = JSON.parse(raw);
      const restoredRows = (state.rows ?? []).map((r) => ({
        ...r,
        uid: r.uid ?? crypto.randomUUID()
      }));
      this.rows.set(restoredRows);
      const storedOptions =
        state.cellOptions ??
        (state as StoredComponentState & { disabledCells?: CellOptions<TableRow>[] })
          .disabledCells ??
        [];
      this.cellOptions.set(
        storedOptions
          .map((opt) => {
            const matchingRow = restoredRows.find((r) => isSameRow(r, opt.row));
            return matchingRow ? { ...opt, row: matchingRow } : null;
          })
          .filter((opt): opt is CellOptions<TableRow> => opt !== null)
      );
      this.siteKindsByUid.set(state.siteKindsByUid ?? {});
      this.isFormExpanded.set(state.isFormExpanded ?? true);
      if (state.selectedDateISO) {
        this.selectedDate.set(new Date(state.selectedDateISO));
      }
      if (state.formValue) {
        this.form.patchValue(state.formValue);
      }
    } catch {
      localStorage.removeItem(this.getStorageKey());
    }
  }

  private clearLocalStorage(): void {
    localStorage.removeItem(this.getStorageKey());
  }

  private executeReset(): void {
    this.rows.set([]);
    this.selectedRowUids.set(new Set<string>());
    this.cellOptions.set([]);
    this.siteKindsByUid.set({});
    this.errors.set([]);
    this.isFormExpanded.set(true);
    this.userSortState.set([]);
    this.searchTerm.set('');
    this.searchResult.set(null);
    this.advancedFilters.set(null);
    this.clearSearchQuery();
    this.pathBar.reset();
    this.selectedDate.set(undefined);
    this.form.reset();
    this.clearLocalStorage();
  }

  // Empty the query the toolbox displays, so the visible query matches the cleared filters.
  // The search query builder only exists once the toolbox has rendered it.
  private clearSearchQuery(): void {
    if (isNil(this.toolboxComponent?.searchQueryBuilder)) {
      return;
    }
    this.toolboxComponent.updateSearchQuery('');
  }

  protected handleRowEdition(rowEvent: RowEvent<TableRow>): void {
    this.saveStateToStorage();
    if (rowEvent.type === 'add') {
      this.rows.update((currentRows) => [...currentRows, this.createDefaultTableRow()]);
    } else if (rowEvent.type === 'edit' && rowEvent.column === 'assigned_vehicle_uid') {
      rowEvent.row.assigned_vehicle_registration = null;
      this.rows.update((rows) => [...rows]);
    } else if (
      rowEvent.type === 'edit' &&
      (rowEvent.column === 'flow_type' || rowEvent.column === 'pickup_site_uid')
    ) {
      if (canExpandManualRow(rowEvent.row)) {
        this.expandManualRow(rowEvent.row);
      }
      this.rows.update((rows) => [...rows]);
    } else if (rowEvent.type === 'delete') {
      this.deleteRows(new Set([rowEvent.row.uid]));
    } else if (
      rowEvent.type === 'edit' &&
      String(rowEvent.column).startsWith('nb_pallets.')
    ) {
      // The cell editor writes into the row in place, which no signal observes. Republish
      // the rows so the sorting, the search and the subtotals — all computed elsewhere —
      // see the new value.
      this.rows.update((rows) => [...rows]);
    }
  }

  // A row added by hand carries no schedule, so its windows, unloading site, commodity and
  // quantity cell options are invented defaults. Once its flow type and loading site are
  // known, the generation can say what lines that pair really stands for: run it scoped to
  // this row and put its lines in the draft's place. The cells the user already typed
  // narrow the generation instead of being overwritten by it.
  private expandManualRow(draft: TableRow): void {
    const dataSpaceId = this.dataspace().dataspace?.id;
    const input = this.transformerInput();
    const flowKindUid = draft.flow_type;
    if (isNil(dataSpaceId) || isNil(input) || isNil(flowKindUid)) {
      return;
    }

    // Captured now: the response lands later, and the row must be recognised by uid rather
    // than by reference, since `loadStateFromStorage` rebuilds the row objects.
    const draftUid = draft.uid;
    const pickupSiteUid = draft.pickup_site_uid;
    const referenceDay = resolveFillReferenceDay((this.form.value as FormData).date);

    this.allSiteSchedules$(dataSpaceId)
      .pipe(
        take(1),
        catchError((err) => {
          console.error('error fetching the site schedules of an added row. err: ', err);
          return of([] as ExtendedSiteSchedule[]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((schedules) => {
        const current = this.rows().find((row) => row.uid === draftUid);
        // A deletion, a new generation, a storage restore or a newer edit on the same two
        // cells all make this response obsolete.
        if (
          isNil(current) ||
          !canExpandManualRow(current) ||
          current.pickup_site_uid !== pickupSiteUid ||
          current.flow_type !== flowKindUid
        ) {
          return;
        }

        const { rows: produced, cellOptions } = makeRowsForSelectedFlowTypes(
          schedules,
          buildManualRowFormData(
            current,
            this.formDataWithUids(this.form.value as FormData),
            referenceDay
          ),
          this.allCommodityUids(),
          this.allJobKindUids(),
          input.flowTypeToUidMap,
          input.commodityAndJobKindColorOptions,
          input.deliveryDelegationLabel
        );
        if (produced.length === 0) {
          // The loading site declares no schedule this flow kind makes coherent. Leave the
          // row alone: its defaults are the only thing left to offer.
          return;
        }

        applyManualRowCells(produced, current);
        this.rows.update((rows) => {
          const index = rows.findIndex((row) => row.uid === draftUid);
          if (index < 0) {
            return rows;
          }
          return [...rows.slice(0, index), ...produced, ...rows.slice(index + 1)];
        });
        this.cellOptions.update((options) => [...options, ...cellOptions]);
      });
  }

  /**
   * One copy per selected row, each placed immediately after the row it came from. Adjacency
   * survives into the grid because `defaultSortingFunction` compares only the sites and the
   * windows, on which a copy ties with its source, and `Array.sort` is stable.
   */
  private duplicateSelectedRows(): void {
    const selected = this.selectedRowUids();
    const currentOptions = this.cellOptions();
    const nextRows: TableRow[] = [];
    const clonedOptions: CellOptions<TableRow>[] = [];

    for (const row of this.rows()) {
      nextRows.push(row);
      if (!selected.has(row.uid)) {
        continue;
      }
      const copy = duplicateRowWithoutQuantities(row);
      nextRows.push(copy);
      clonedOptions.push(...cloneCellOptionsForRow(currentOptions, row, copy));
    }

    if (clonedOptions.length > 0) {
      this.cellOptions.set([...currentOptions, ...clonedOptions]);
    }
    this.rows.set(nextRows);
  }

  private deleteSelectedRows(): void {
    this.deleteRows(this.selectedRowUids());
  }

  /**
   * Removes rows by uid rather than by the position the table reports: the grid is fed
   * `filteredAndSortedRows`, so an index counts in the displayed order, which a sort or a
   * search makes differ from `rows`.
   *
   * The `CellOptions` of a removed row go with it — the table resolves them by row reference,
   * so they would otherwise sit in the state, and in storage, matching nothing.
   */
  private deleteRows(uids: ReadonlySet<string>): void {
    if (uids.size === 0) {
      return;
    }
    this.rows.update((rows) => rows.filter((row) => !uids.has(row.uid)));
    this.cellOptions.update((options) =>
      options.filter((option) => !uids.has(option.row.uid))
    );
    this.selectedRowUids.update((selected) => {
      const next = new Set(selected);
      uids.forEach((uid) => next.delete(uid));
      return next;
    });
  }

  /**
   * Writes onto the selected rows in place. Replacing the row objects instead would orphan every
   * `CellOptions` and `TableRowError` entry, which the table resolves by row reference.
   */
  private assignToSelectedRows(apply: (row: TableRow) => void): void {
    const selected = this.selectedRowUids();
    for (const row of this.rows()) {
      if (selected.has(row.uid)) {
        apply(row);
      }
    }
    this.rows.update((rows) => [...rows]);
  }

  /**
   * The value the whole selection agrees on. `agreed` is reported separately from `value`
   * because a selection that agrees on "no value" and one that is split both have no value to
   * report, and only the first of the two deserves a check mark on the "no value" entry.
   */
  private sharedValueAcrossSelection<K extends keyof TableRow>(
    key: K
  ): { agreed: boolean; value: TableRow[K] | null } {
    const rows = this.selectedRows();
    if (rows.length === 0) {
      return { agreed: false, value: null };
    }
    const first = rows[0][key];
    return rows.every((row) => row[key] === first)
      ? { agreed: true, value: first }
      : { agreed: false, value: null };
  }

  private createDefaultTableRow(): TableRow {
    const nb_pallets: Record<string, string> = {};

    // Populate with all job kind UIDs (all enabled with empty string)
    for (const uid of this.allJobKindUids()) {
      nb_pallets[uid] = '';
    }

    return {
      uid: crypto.randomUUID(),
      pickup_site_uid: '',
      pickup_window_start: { ...DEFAULT_ROW_WINDOW_START },
      pickup_window_finish: { ...DEFAULT_ROW_WINDOW_FINISH },
      delivery_site_uid: '',
      delivery_window_start: { ...DEFAULT_ROW_WINDOW_START },
      delivery_window_finish: { ...DEFAULT_ROW_WINDOW_FINISH },
      flow_type: undefined,
      commodity: '',
      nb_pallets,
      assigned_transporter_uid: null,
      assigned_vehicle_uid: null,
      assigned_vehicle_registration: null,
      isManualDraft: true
    };
  }

  protected handleSortChange(sortState: SortState<TableRow>[]): void {
    this.userSortState.set(sortState);
  }

  protected onAdvancedQuerySearch(result: SearchResult): void {
    this.searchResult.set(result);

    // Clear simple search when using advanced search
    if (
      result &&
      (Object.keys(result.qualifiers).length > 0 || result.textTerms.length > 0)
    ) {
      this.searchTerm.set('');
    }
  }

  protected toggleFormExpansion() {
    this.isFormExpanded.update((value) => !value);
  }

  protected reIndexErrors(indexes: number[]) {
    this.currentIndexMap.set(indexes);
  }

  protected formInputHasError(fieldId: string): boolean {
    const control = this.form.get(fieldId);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  protected getInputErrorCode(id: string): string {
    const control = this.form.get(id);
    if (!control || !control.errors) return '';
    return Object.keys(control.errors)[0];
  }

  protected getInputErrorParams(fieldId: string): object {
    const control = this.form.get(fieldId);
    if (!control || !control.errors) {
      return {};
    }

    const firstErrorKey = Object.keys(control.errors)[0];
    return control.errors[firstErrorKey];
  }

  protected handleGenerationClick(): void {
    if (!this.isFormValid()) {
      this.notificationService.error('Invalid form', 'The generation form is not valid');
      return;
    }
    if (this.rows().length > 0) {
      this.openConfirmModal(
        'job_assisted_importation.confirm_generate.title',
        'job_assisted_importation.confirm_generate.description',
        () => this.executeGeneration()
      );
      return;
    }
    this.executeGeneration();
  }

  private executeGeneration(): void {
    this.selectedDate.set(undefined);
    const dataspaceId = this.dataspace().dataspace?.id;
    if (isNil(dataspaceId)) {
      this.notificationService.error(
        'Inconsistent application statte',
        'Dataspace is not defined'
      );
      return;
    }
    const input = this.transformerInput();
    if (isNil(input)) {
      this.notificationService.error(
        'Invalid application state',
        'Transformer configuration is undefined'
      );
      return;
    }

    // Retrieve the form details filled by the user
    const rawFormData = this.form.value as FormData;

    const rawDate = rawFormData.date;
    if (isNil(rawDate) || rawDate === '') {
      this.notificationService.error(
        'Invalid form value',
        'Date was not provided or in invalid format'
      );
      return;
    }
    const date = new Date(rawDate);

    const formData = this.formDataWithUids(rawFormData);

    const allCommodityUids = this.allCommodityUids();
    const allJobKindUids = this.allJobKindUids();

    // Build the semantic UID mapping for flow kinds
    // FlowType enum values are the French names that map to UIDs
    const semanticFlowKindUids = input.flowTypeToUidMap;

    const isDeliveryAndReverseLogisticsCase = isDeliveryAndReverseLogisticsSelection(
      formData.flow_types,
      semanticFlowKindUids
    );

    if (
      !isDeliveryAndReverseLogisticsCase &&
      !isNil(formData.flow_types) &&
      formData.flow_types.length > 1
    ) {
      this.notificationService.error(
        'Invalid form value',
        'Cannot combine flow types other than delivery and reverse logistics'
      );
      return;
    }

    // Fetch all schedules from database matching the site selection
    this.fetchSchedules(formData, dataspaceId)
      .pipe(
        take(1),
        // Filter according to flow types
        map((schedules) =>
          makeRowsForSelectedFlowTypes(
            schedules,
            formData,
            allCommodityUids,
            allJobKindUids,
            semanticFlowKindUids,
            input.commodityAndJobKindColorOptions,
            input.deliveryDelegationLabel
          )
        ),
        tap(({ rows, cellOptions }) => {
          this.rows.set(rows);
          this.selectedRowUids.set(new Set<string>());
          this.cellOptions.set(cellOptions);
          this.errors.set([]);
          this.isFormExpanded.set(false);
          this.selectedDate.set(date);
          this.searchTerm.set('');
        }),
        catchError((err, caught) => {
          console.error('error generating OT rows. err: ', err);
          console.error('error generating OT rows. caught: ', caught);
          return of(null);
        })
      )
      .subscribe();
    return;
  }

  private fetchSchedules(
    formData: FormData,
    dataSpaceId: number
  ): Observable<ExtendedSiteSchedule[]> {
    // Select only the concerned sites to avoid fetching unnecessary schedules. We can only
    // filter the sites if both pickup and delivery locations are constrained, otherwise all
    // sites are potentially useful
    let uids = undefined;
    if (
      isDefinedAndNotEmpty(formData.pickup_site_uids) &&
      isDefinedAndNotEmpty(formData.delivery_site_uids)
    ) {
      uids = dedup(formData.pickup_site_uids.concat(formData.delivery_site_uids));
    }

    return this.fetchSiteSchedules$(uids, dataSpaceId).pipe(
      tap(({ siteKindsByUid }) => this.siteKindsByUid.set(siteKindsByUid)),
      map(({ schedules }) => schedules)
    );
  }

  // Every site's opening schedules, fetched once per session and shared. Expanding an
  // added row is driven by the unloading schedules, so it needs the schedules of sites the
  // row does not name — the narrowing `fetchSchedules` applies would hide them.
  private allSiteSchedules$(dataSpaceId: number): Observable<ExtendedSiteSchedule[]> {
    const cached = this.allSchedules;
    if (!isNil(cached)) {
      return cached;
    }

    const request$ = this.fetchSiteSchedules$(undefined, dataSpaceId).pipe(
      // Merge rather than replace: a lookup made for one added row must not drop the site
      // kinds the generation collected, which the default row ordering still reads.
      tap(({ siteKindsByUid }) =>
        this.siteKindsByUid.update((kinds) => ({ ...kinds, ...siteKindsByUid }))
      ),
      map(({ schedules }) => schedules),
      // Drop the cache on failure, or every later row would replay the same error instead
      // of retrying.
      catchError((err) => {
        this.allSchedules = undefined;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.allSchedules = request$;
    return request$;
  }

  // Every site schedule read goes through here: it owns the selector request shape. An
  // undefined `siteUids` fetches every site.
  private fetchSiteSchedules$(
    uids: string[] | undefined,
    dataSpaceId: number
  ): Observable<DecoratedSiteSchedules> {
    const selectRequest: EntitySelectorRequestModel = {
      entityType: 'site',
      entityTypeVersion: 'UserSite',
      dataSpaceId,
      filters:
        uids && uids.length > 0
          ? uidListFilter('job-import-sites', 'uid', uids, 'user-site-v1')
          : undefined
    };
    return this.selector
      .selectAllData$<UserSiteV1Model>(selectRequest, { maxItems: 1000 })
      .pipe(
        take(1),
        map((res) => decorateSiteSchedules(res.items))
      );
  }

  private handleTransformerErrors(err: GbeError) {
    const messages = this.gbeErrorParserService
      .resolveError(err)
      .flatMap((view) => view.occurrences)
      .map((occurrence) => occurrenceText(occurrence));

    const errors: TableRowError<TableRow>[] = [];
    for (const message of messages) {
      const rowError: TableRowError<TableRow> = {
        row: {
          uid: crypto.randomUUID(),
          pickup_site_uid: '',
          pickup_window_start: { hours: 0, minutes: 0 },
          pickup_window_finish: { hours: 0, minutes: 0 },
          delivery_site_uid: '',
          delivery_window_start: { hours: 0, minutes: 0 },
          delivery_window_finish: { hours: 0, minutes: 0 },
          commodity: '',
          nb_pallets: {},
          assigned_transporter_uid: null,
          assigned_vehicle_uid: null,
          assigned_vehicle_registration: null
        },
        completeDetails: false,
        backendError: true,
        message
      };
      errors.push(rowError);
    }

    this.errors.set(errors);
  }

  private handleDataUploadWithTransformer(): void {
    const data = this.rows();
    const formData = this.form.value as FormData;
    const input = this.transformerInput();

    if (isNil(input)) {
      this.notificationService.error(
        'Invalid application state',
        'Transformer configuration is undefined'
      );
      return;
    }

    const transformer: Transformer = {
      name: 'UserJobTransportOrder',
      version: '1',
      generatedEntities: ['JOB']
    };

    const transformerData: DataTransformerRequest<UserJobTransportOrderV1Model> = {
      transformer: transformer,
      dataModels: [
        {
          data: convertKeysToSnakeCase(
            makeTransportOrders(data, formData, input, this.kgPerPalletByCommodity())
          ),
          entityTypeVersion: 'UserJobTransportOrder'
        }
      ]
    };
    this.csvTransformersService
      .postTransformers(transformerData, this.dataspace().dataspace?.id ?? undefined)
      .pipe(
        take(1),
        tap(() => {
          this.isUploading.set(false);
          this.clearLocalStorage();
          this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
        }),
        catchError((err) => {
          console.error('transformer errors: ', err);
          if (err.errorCode === GbeErrorCodes.MULTIPLE_ERRORS) {
            this.handleTransformerErrors(err);
          }
          this.isUploading.set(false);
          return of(null);
        })
      )
      .subscribe();
    return;
  }

  // The generation form holds the commodity, order type and flow kind display names, while
  // the schedules and the grid rows carry uids. Every consumer of the form filters on uids,
  // so the translation happens once, here.
  private formDataWithUids(rawFormData: FormData): FormData {
    return {
      ...rawFormData,
      commodities: this.convertCommodityNamesToUids(rawFormData.commodities),
      job_kinds: this.convertJobKindNamesToUids(rawFormData.job_kinds),
      flow_types: this.convertFlowKindNamesToUids(rawFormData.flow_types)
    };
  }

  private convertCommodityNamesToUids(names: string[] | undefined): string[] | undefined {
    if (isNilOrEmpty(names)) {
      return names;
    }
    const map = this.commodityNameToUidMap();
    return names.map((name) => map.get(name) ?? name);
  }

  private convertJobKindNamesToUids(names: string[] | undefined): string[] | undefined {
    if (isNilOrEmpty(names)) {
      return names;
    }
    const map = this.jobKindNameToUidMap();
    return names.map((name) => map.get(name) ?? name);
  }

  private convertFlowKindNamesToUids(names: string[] | undefined): string[] | undefined {
    if (isNilOrEmpty(names)) {
      return names;
    }
    const map = this.flowKindNameToUidMap();
    return names.map((name) => map.get(name) ?? name);
  }

  protected addFilterOn(column: keyof TableRow): void {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column);
      if (field) this.openPillFor(field);
      return;
    }

    const condition = createFilterCondition(
      findFilterField(this.availableAdvancedFilters(), String(column))
    );
    if (isNil(condition)) {
      return;
    }

    this.advancedFilters.set(
      addOrReplaceConditionInConfig(this.advancedFilters(), condition)
    );
    this.openFilterForField.set(condition.id);
    setTimeout(() => {
      this.openFilterForField.set(null);
    }, 100);
  }

  protected removeFilterOn(column: keyof TableRow): void {
    if (this.activeGrammar() === 'path') {
      const field = this.pathFieldFor(column);
      if (field) this.pathBar.removeConditionsOn(field);
      return;
    }

    this.advancedFilters.set(
      removeConditionFromConfig(this.advancedFilters(), String(column))
    );
  }

  protected onFilterChanges(config: FilterConfiguration): void {
    this.advancedFilters.set(config);
  }

  /** The pills row sits behind the funnel, so it is shown before the field's popover opens. */
  private openPillFor(field: FilterField): void {
    this.showAdvancedFiltering.set(true);
    afterNextRender(() => this.pills()?.openField(field), { injector: this.injector });
  }
  private pathFieldFor(column: keyof TableRow): FilterField | undefined {
    return this.pathBar.fieldForColumn(String(column));
  }

  private applySearchFiltering(rows: TableRow[], result: SearchResult): TableRow[] {
    let filtered = rows;

    // Apply qualifier filtering
    for (const [qualifier, values] of Object.entries(result.qualifiers)) {
      filtered = filtered.filter((row) => this.matchesQualifier(row, qualifier, values));
    }

    // Apply text term filtering
    if (result.textTerms.length > 0) {
      filtered = filtered.filter((row) =>
        result.textTerms.every((term) => this.rowMatchesTextTerm(row, term))
      );
    }

    return filtered;
  }

  private matchesQualifier(
    row: TableRow,
    qualifier: string,
    values: QualifierValue[]
  ): boolean {
    const negatedValues = values.filter((v) => v.negated).map((v) => v.value);
    const positiveValues = values.filter((v) => !v.negated).map((v) => v.value);

    // If ANY negated value matches, exclude the row
    for (const negatedValue of negatedValues) {
      if (this.fieldMatchesValue(row, qualifier, negatedValue)) {
        return false;
      }
    }

    // If there are positive values, at least ONE must match
    if (positiveValues.length > 0) {
      return positiveValues.some((value) => this.fieldMatchesValue(row, qualifier, value));
    }

    // If only negated values were specified, the row passes (wasn't excluded above)
    return true;
  }

  private fieldMatchesValue(row: TableRow, qualifier: string, value: string): boolean {
    const searchValue = foldForSearch(value);

    switch (qualifier) {
      case 'site_collecte':
        return foldForSearch(row.pickup_site_uid).includes(searchValue);

      case 'site_livraison':
        return foldForSearch(row.delivery_site_uid).includes(searchValue);

      case 'site_preparation':
        return row.preparing_site_uid
          ? foldForSearch(row.preparing_site_uid).includes(searchValue)
          : false;

      case 'site_commande':
        return row.ordering_site_uid
          ? foldForSearch(row.ordering_site_uid).includes(searchValue)
          : false;

      case 'flux':
        return row.flow_type ? foldForSearch(row.flow_type).includes(searchValue) : false;

      case 'marchandise':
        return foldForSearch(row.commodity).includes(searchValue);

      case 'heure_debut_collecte':
        return this.matchesTimeValue(row.pickup_window_start, searchValue);

      case 'heure_fin_collecte':
        return this.matchesTimeValue(row.pickup_window_finish, searchValue);

      case 'heure_debut_livraison':
        return this.matchesTimeValue(row.delivery_window_start, searchValue);

      case 'heure_fin_livraison':
        return this.matchesTimeValue(row.delivery_window_finish, searchValue);

      case 'transporteur':
        return row.assigned_transporter_uid
          ? foldForSearch(row.assigned_transporter_uid).includes(searchValue)
          : false;

      case 'vehicule':
        return row.assigned_vehicle_uid
          ? foldForSearch(row.assigned_vehicle_uid).includes(searchValue)
          : false;

      case 'immatriculation':
        return row.assigned_vehicle_registration
          ? foldForSearch(row.assigned_vehicle_registration).includes(searchValue)
          : false;

      case 'palettes': {
        // Search in both job kind UIDs (keys) and quantities (values)
        const jobKindUids = foldForSearch(Object.keys(row.nb_pallets).join(' '));
        const quantities = foldForSearch(Object.values(row.nb_pallets).join(' '));
        return jobKindUids.includes(searchValue) || quantities.includes(searchValue);
      }

      default:
        return false;
    }
  }

  // Formatted through the shared reader rather than read field by field: a bound the
  // stored checkpoint cannot produce a time from matches nothing instead of throwing while
  // the user types into the column filter.
  private matchesTimeValue(time: TimeValue, searchValue: string): boolean {
    const formatted = formatTime(time);
    return formatted !== '' && formatted.includes(searchValue);
  }

  private rowMatchesTextTerm(row: TableRow, term: string): boolean {
    const search = foldForSearch(term);

    // Search in site uids, commodities, and vehicle/transporter
    if (
      foldForSearch(row.pickup_site_uid).includes(search) ||
      foldForSearch(row.delivery_site_uid).includes(search) ||
      foldForSearch(row.preparing_site_uid).includes(search) ||
      foldForSearch(row.ordering_site_uid).includes(search) ||
      foldForSearch(row.commodity).includes(search) ||
      (!isNil(row.flow_type) && foldForSearch(row.flow_type).includes(search)) ||
      (row.assigned_transporter_uid &&
        foldForSearch(row.assigned_transporter_uid).includes(search)) ||
      (!isNil(row.assigned_vehicle_uid) &&
        foldForSearch(row.assigned_vehicle_uid).includes(search))
    ) {
      return true;
    }

    // Search in time windows (formatted as HH:MM)
    if (
      formatTime(row.pickup_window_start).includes(search) ||
      formatTime(row.pickup_window_finish).includes(search) ||
      formatTime(row.delivery_window_start).includes(search) ||
      formatTime(row.delivery_window_finish).includes(search)
    ) {
      return true;
    }

    // Search in job kind UIDs
    const jobKindUids = foldForSearch(Object.keys(row.nb_pallets).join(' '));
    if (jobKindUids.includes(search)) return true;

    // Search in pallet quantities
    const quantities = foldForSearch(Object.values(row.nb_pallets).join(' '));
    return quantities.includes(search);
  }
}

function isSameRow(a: TableRow, b: TableRow): boolean {
  return a.uid === b.uid;
}

interface JobKindAndQuantity {
  jobKindUid: string;
  quantity: number;
}

export function parseNumber(value: string): number | undefined {
  if (value.trim().match(numberRegex)) {
    // Handle comma decimal separator (e.g., "123,45" → 123.45)
    const normalizedValue = value.replace(',', '.');
    const numValue = Number(normalizedValue);
    if (!isNaN(numValue)) {
      return numValue;
    }
  }
  return undefined;
}

/**
 * Quantity a `nb_pallets` cell carries, under the single rule the import applies: blanks,
 * unparsable entries and non-positive numbers carry nothing. The displayed subtotals read
 * cells through it too, so what the footer counts is what gets uploaded.
 */
export function palletQuantity(value: string | undefined): number | undefined {
  const quantity = parseNumber(value ?? '');
  return isNil(quantity) || quantity <= 0 ? undefined : quantity;
}

export function makeQuantVec(row: TableRow): JobKindAndQuantity[] {
  const quants: JobKindAndQuantity[] = [];

  for (const [jobKindUid, valueStr] of Object.entries(row.nb_pallets)) {
    const quantity = palletQuantity(valueStr);
    if (!isNil(quantity)) {
      quants.push({ jobKindUid, quantity });
    }
  }

  return quants;
}

// date is in yyyy-mm-dd
export function makeDateTimeISO(date: string, time: TimeValue): string {
  const hours = String(time.hours).padStart(2, '0');
  const minutes = String(time.minutes).padStart(2, '0');
  return `${date}T${hours}:${minutes}:00`;
}

function parseRegistration(
  value: string | null
): UserVehicleRegistrationAssignment | undefined {
  if (!value) return undefined;
  if (value.startsWith('copy:')) return { copy: parseInt(value.slice(5), 10) };
  if (value.startsWith('code:')) return { code: value.slice(5) };
  return undefined;
}

// Weight (kg) of one pallet per commodity uid, read from the `labelKey` custom label.
// Commodities whose label is missing or not a positive number are left out so they fall
// back to the configured weights. A decimal comma is accepted, as users type French numbers.
export function buildKgPerPalletByCommodity(
  commodities: UserCommodityV1Model[],
  labelKey: string
): Record<string, number> {
  const kgPerPallet: Record<string, number> = {};
  for (const commodity of commodities) {
    // Number('') is 0 and Number(undefined) is NaN, so blank and missing labels are
    // rejected by the positivity check below.
    const value = Number(
      commodity.custom_data?.labels?.[labelKey]?.trim().replace(',', '.')
    );
    if (Number.isFinite(value) && value > 0) {
      kgPerPallet[commodity.uid] = value;
    }
  }
  return kgPerPallet;
}

// Weight (kg) of a single pallet for the given commodity. A weight read from the commodity
// custom label wins; otherwise water and the small-pallet commodities have dedicated
// weights, and anything else falls back to the default weight.
export function getKgPerPallet(
  commodityUid: string,
  input: TransformerInput,
  kgPerPalletByCommodity: Record<string, number> = {}
): number {
  if (Object.hasOwn(kgPerPalletByCommodity, commodityUid)) {
    return kgPerPalletByCommodity[commodityUid];
  }
  if (commodityUid === input.waterCommodityUid) {
    return input.waterKgPerPallet;
  }
  if (input.smallPalletCommodityUids.includes(commodityUid)) {
    return input.freshKgPerPallet;
  }
  return input.defaultKgPerPallet;
}

// Builds an operation datetime window, replicating any inherited softness
// (earliness/lateness). A null/undefined config is omitted so the corresponding
// bound stays hard.
export function makeOperationWindow(
  start: string,
  finish: string,
  earlinessCfg?: EarlinessCfg | null,
  latenessCfg?: LatenessCfg | null
): DateTimeWindow {
  const window: DateTimeWindow = { start, finish };
  if (!isNil(earlinessCfg)) {
    window.earliness_cfg = earlinessCfg;
  }
  if (!isNil(latenessCfg)) {
    window.lateness_cfg = latenessCfg;
  }
  return window;
}

export function makeTransportOrders(
  data: TableRow[],
  formData: FormData,
  input: TransformerInput,
  kgPerPalletByCommodity: Record<string, number> = {}
): UserJobTransportOrderV1InputModel[] {
  const date = formData.date;
  if (isNil(date) || date === '') {
    return [];
  }
  return data.flatMap((row) => {
    const pickupWindowStart = makeDateTimeISO(date, row.pickup_window_start);
    const pickupWindowFinish = makeDateTimeISO(date, row.pickup_window_finish);
    const deliveryWindowStart = makeDateTimeISO(date, row.delivery_window_start);
    const deliveryWindowFinish = makeDateTimeISO(date, row.delivery_window_finish);

    return makeQuantVec(row).map((quantAndJobKind) => {
      const pickSecs = Math.ceil(input.nbSecsPerPickupPallet * quantAndJobKind.quantity);
      const delivSecs = Math.ceil(input.nbSecsPerDeliveryPallet * quantAndJobKind.quantity);

      // Replicate the softness inherited from the pickup/delivery site schedules
      // onto the generated operation windows. Absent earliness/lateness keeps the
      // corresponding window bound hard.
      const pickupWindow = makeOperationWindow(
        pickupWindowStart,
        pickupWindowFinish,
        row.pickup_earliness_cfg,
        row.pickup_lateness_cfg
      );
      const deliveryWindow = makeOperationWindow(
        deliveryWindowStart,
        deliveryWindowFinish,
        row.delivery_earliness_cfg,
        row.delivery_lateness_cfg
      );

      const pickup: Operation = {
        datetime_windows: [pickupWindow],
        site: {
          specific_site: row.pickup_site_uid
        },
        duration: 'PT' + pickSecs.toFixed() + 'S'
      };

      const delivery: Operation = {
        datetime_windows: [deliveryWindow],
        site: {
          specific_site: row.delivery_site_uid
        },
        duration: 'PT' + delivSecs.toFixed() + 'S'
      };

      const capUid = input.smallPalletCommodityUids.includes(row.commodity)
        ? input.smallPalletCapUid
        : input.largePalletCapUid;
      const quantity = { entity_uid: capUid, value: quantAndJobKind.quantity };

      // Weight (kg) requirement: each pallet contributes a commodity-dependent weight
      const weightQuantity = {
        entity_uid: input.weightCapUid,
        value:
          getKgPerPallet(row.commodity, input, kgPerPalletByCommodity) *
          quantAndJobKind.quantity
      };

      // E01→0085 27 Pal PP PGC
      // {pickup_site}->{delivery_site} {quantity} Pal {job_kind} {commodity}
      const name = `${row.pickup_site_uid}→${row.delivery_site_uid} ${quantAndJobKind.quantity} Pal ${quantAndJobKind.jobKindUid} ${row.commodity}`;
      const flowKindUid = row.flow_type;
      const isReverseLogistics =
        !isNil(flowKindUid) && flowKindUid == input.flowTypeToUidMap.reverseLogistics;

      return {
        name,
        pickup: [pickup],
        delivery: [delivery],
        transported_commodity_uid: row.commodity,
        kind_uid: quantAndJobKind.jobKindUid,
        is_reverse_logistics: isReverseLogistics,
        expertise_requirements: [],
        vehicle_capacity_requirement: [quantity, weightQuantity],
        vehicle_pre_assignments: isNil(row.assigned_vehicle_uid)
          ? undefined
          : [
              {
                vehicle_uid: row.assigned_vehicle_uid,
                registration: parseRegistration(row.assigned_vehicle_registration)
              }
            ],
        pre_assigned_company_uids: isNil(row.assigned_transporter_uid)
          ? undefined
          : [row.assigned_transporter_uid],
        preparing_site_uid: describedSiteUid(row.preparing_site_uid, row.pickup_site_uid),
        ordering_site_uid: describedSiteUid(row.ordering_site_uid, row.delivery_site_uid),
        custom_data: { tags: [], labels: {}, other_data: {} },
        kpis: []
      };
    });
  });
}

export function filterSchedules(
  schedules: ExtendedSiteSchedule[],
  formData: FormData,
  semanticFlowKindUids: SemanticFlowKindUids
): ExtendedSiteSchedule[] {
  return schedules.filter((schedule: ExtendedSiteSchedule) => {
    // Remove schedules that do not match the desired date
    if (!isNil(formData.date)) {
      const date = new Date(formData.date);
      if (isNil(date) || !matchDate(schedule.window.recurrence, date)) {
        return false;
      }
    }

    // Remove schedules that are not compatible with any of the desired flow kinds
    // Uses UID-based intersection with compatibleFlowKindUids
    if (
      isDefinedAndNotEmpty(formData.flow_types) &&
      isDefinedAndNotEmpty(schedule.compatible_flow_kind_uids)
    ) {
      const scheduleFlowKindUids = new Set(schedule.compatible_flow_kind_uids);
      // If the schedule has no compatible flow kinds, it's unrestricted
      const hasIntersection = !isNil(
        formData.flow_types.find((flowKindUid) => scheduleFlowKindUids.has(flowKindUid))
      );
      if (!hasIntersection) {
        return false;
      }
    }

    // Remove schedules that are not compatible with any of the desired job kinds
    if (
      isDefinedAndNotEmpty(formData.job_kinds) &&
      isDefinedAndNotEmpty(schedule.compatible_job_kind_uids)
    ) {
      const scheduleJobKinds = new Set(schedule.compatible_job_kind_uids ?? []);
      const hasIntersection = !isNil(
        formData.job_kinds.find((kind) => scheduleJobKinds.has(kind))
      );
      if (!hasIntersection) {
        return false;
      }
    }

    // Remove schedules that are not compatible with any of the desired commodities
    if (
      isDefinedAndNotEmpty(formData.commodities) &&
      isDefinedAndNotEmpty(schedule.compatible_commodity_uids)
    ) {
      const scheduleCommodities = new Set(schedule.compatible_commodity_uids);
      const hasIntersection = !isNil(
        formData.commodities.find((commodity) => scheduleCommodities.has(commodity))
      );
      if (!hasIntersection) {
        return false;
      }
    }

    // Helper to check if schedule has a specific semantic flow kind
    const formIncludesFlowKind = (uid: string | undefined): boolean =>
      !isNil(uid) && !isNil(formData.flow_types) && formData.flow_types.includes(uid);
    const siteIsClient = schedule.siteKinds.includes(SiteKind.CLIENT);
    const siteIsWarehouse =
      schedule.siteKinds.includes(SiteKind.WAREHOUSE) ||
      schedule.siteKinds.includes(SiteKind.VEHICLE_BASE);
    const siteIsProvider = schedule.siteIsProvider;
    const isPurePickup =
      isScheduleCompatibleWithPickup(schedule) &&
      !isScheduleCompatibleWithDelivery(schedule);
    const isPureDelivery =
      isScheduleCompatibleWithDelivery(schedule) &&
      !isScheduleCompatibleWithPickup(schedule);

    // Remove pickup schedules on sites not listed in the list of desired pickup sites
    if (isDefinedAndNotEmpty(formData.pickup_site_uids) && isPurePickup) {
      if (!formData.pickup_site_uids.includes(schedule.siteUid)) {
        return false;
      }
    }

    // Remove delivery schedules on sites not listed in the list of desired delivery sites
    if (isDefinedAndNotEmpty(formData.delivery_site_uids) && isPureDelivery) {
      if (!formData.delivery_site_uids.includes(schedule.siteUid)) {
        return false;
      }
    }

    // handle flow type and schedule kind coherence
    if (isDefinedAndNotEmpty(formData.flow_types) && (isPurePickup || isPureDelivery)) {
      // Flow kinds for which the schedule makes sense, given the kinds of its site:
      //   - a client is loaded to bring goods back (REVERSE_LOGISTICS) and unloaded to
      //     receive them (DELIVERY);
      //   - a warehouse is loaded to serve clients (DELIVERY) and unloaded by the returns
      //     it collects (REVERSE_LOGISTICS, FROM_PROVIDERS_TO_WAREHOUSES);
      //   - a provider is only loaded, to supply the warehouses
      //     (FROM_PROVIDERS_TO_WAREHOUSES).
      // A site frequently holds several kinds at once (a warehouse that also takes client
      // returns, for instance), so a schedule stays relevant as soon as ONE of its site
      // kinds makes it so.
      const relevantFlowKindUids: (string | undefined)[] = [];
      if (siteIsClient) {
        relevantFlowKindUids.push(
          isPurePickup
            ? semanticFlowKindUids.reverseLogistics
            : semanticFlowKindUids.delivery
        );
      }
      if (siteIsWarehouse) {
        if (isPurePickup) {
          relevantFlowKindUids.push(semanticFlowKindUids.delivery);
        } else {
          relevantFlowKindUids.push(
            semanticFlowKindUids.reverseLogistics,
            semanticFlowKindUids.fromProvidersToWarehouses
          );
        }
      }
      if (siteIsProvider && isPurePickup) {
        relevantFlowKindUids.push(semanticFlowKindUids.fromProvidersToWarehouses);
      }

      // A site whose kinds are all unknown carries no expectation, so its schedules are
      // left untouched. A provider-only delivery, on the other hand, is knowingly
      // irrelevant and ends up with an empty list.
      const siteHasKnownKind = siteIsClient || siteIsWarehouse || siteIsProvider;
      if (siteHasKnownKind && !relevantFlowKindUids.some(formIncludesFlowKind)) {
        return false;
      }
    }

    const scheduleWindowStartAndFinish = makeStartAndFinishTimeValues(
      schedule.window.time_window
    );

    if (!isNil(scheduleWindowStartAndFinish)) {
      const [scheduleWindowStart, scheduleWindowFinish] = scheduleWindowStartAndFinish;

      // Remove schedules for which the end is before the desired start
      if (!isNil(formData.time_begin)) {
        const desiredWindowStart = processTimeValue(formData.time_begin) as
          | TimeValue
          | undefined;
        if (!isNil(desiredWindowStart) && !isNil(scheduleWindowFinish)) {
          if (scheduleWindowFinish.hours < desiredWindowStart.hours) {
            return false;
          }
          if (
            scheduleWindowFinish.hours == desiredWindowStart.hours &&
            scheduleWindowFinish.minutes < desiredWindowStart.minutes
          ) {
            return false;
          }
        }
      }

      // Remove schedules for which the start is after the desired finish
      if (!isNil(formData.time_end)) {
        const desiredWindowFinish = processTimeValue(formData.time_end) as
          | TimeValue
          | undefined;
        if (!isNil(desiredWindowFinish) && !isNil(scheduleWindowStart)) {
          if (scheduleWindowStart.hours > desiredWindowFinish.hours) {
            return false;
          }
          if (
            scheduleWindowStart.hours == desiredWindowFinish.hours &&
            scheduleWindowStart.minutes > desiredWindowFinish.minutes
          ) {
            return false;
          }
        }
      }
    }
    return true;
  });
}

export function areMatchingSchedules(
  pickupSchedule: ExtendedSiteSchedule,
  deliverySchedule: ExtendedSiteSchedule
): boolean {
  // check that they are at different sites
  if (pickupSchedule.siteUid === deliverySchedule.siteUid) {
    return false;
  }

  // check that pickupSchedule is really a pickup schedule
  const pickupScheduleIsReallyPickup = isScheduleCompatibleWithPickup(pickupSchedule);
  if (!pickupScheduleIsReallyPickup) {
    return false;
  }

  // check that deliverySchedule is really a delivery schedule
  const deliveryScheduleIsReallyDelivery =
    isScheduleCompatibleWithDelivery(deliverySchedule);
  if (!deliveryScheduleIsReallyDelivery) {
    return false;
  }

  // check that both schedules share at least one flow kind if constrained
  const pickupFlowKindUids = pickupSchedule.compatible_flow_kind_uids;
  const deliveryFlowKindUids = deliverySchedule.compatible_flow_kind_uids;
  if (
    isDefinedAndNotEmpty(pickupFlowKindUids) &&
    isDefinedAndNotEmpty(deliveryFlowKindUids)
  ) {
    const dontShareFlowKind = isNil(
      pickupFlowKindUids.find((uid) => deliveryFlowKindUids.includes(uid))
    );
    if (dontShareFlowKind) {
      return false;
    }
  }

  // check that both schedules share at least one commodity if constrained
  const pickupCommodities = pickupSchedule.compatible_commodity_uids;
  const deliveryCommodities = deliverySchedule.compatible_commodity_uids;
  if (
    isDefinedAndNotEmpty(pickupCommodities) &&
    isDefinedAndNotEmpty(deliveryCommodities)
  ) {
    const dontShareCommodity = isNil(
      pickupCommodities.find((pickupCommodity) =>
        deliveryCommodities.includes(pickupCommodity)
      )
    );
    if (dontShareCommodity) {
      return false;
    }
  }

  // check that both schedules share at least one job kind if constrained
  const pickupJobKind = pickupSchedule.compatible_job_kind_uids;
  const deliveryJobKind = deliverySchedule.compatible_job_kind_uids;
  if (isDefinedAndNotEmpty(pickupJobKind) && isDefinedAndNotEmpty(deliveryJobKind)) {
    const dontShareJobKind = isNil(
      pickupJobKind.find((pickupJobKind) => deliveryJobKind.includes(pickupJobKind))
    );
    if (dontShareJobKind) {
      return false;
    }
  }

  // Check the compatible delivery sites from the pickup schedule if constrained
  if (isDefinedAndNotEmpty(pickupSchedule.compatible_delivery_site_uids)) {
    if (!pickupSchedule.compatible_delivery_site_uids.includes(deliverySchedule.siteUid)) {
      return false;
    }
  }

  // Check the compatible pickup sites from the delivery schedule if constrained
  if (isDefinedAndNotEmpty(deliverySchedule.compatible_pickup_site_uids)) {
    if (!deliverySchedule.compatible_pickup_site_uids.includes(pickupSchedule.siteUid)) {
      return false;
    }
  }

  return true;
}

// Start and finish bounds of a schedule time window.
export interface ScheduleWindowBounds {
  start: TimeValue;
  finish: TimeValue;
}

function timeValueToMinutes(time: TimeValue): number {
  return time.hours * 60 + time.minutes;
}

// Idle time, in minutes, between the end of a loading window and the start of a delivery
// window. Zero when the two windows overlap, since loading can then happen right before
// the delivery.
function pickupToDeliveryGapMinutes(
  pickupWindowFinish: TimeValue,
  deliveryWindowStart: TimeValue
): number {
  return Math.max(
    0,
    timeValueToMinutes(deliveryWindowStart) - timeValueToMinutes(pickupWindowFinish)
  );
}

// Whether `candidate` is a better loading window than `incumbent` to serve a delivery
// starting at `deliveryWindowStart`. The window leaving the least idle time before the
// delivery wins; equally close windows are separated by the one starting last, which is
// the nearest to the delivery.
export function isPickupCloserToDelivery(
  candidate: ScheduleWindowBounds,
  incumbent: ScheduleWindowBounds,
  deliveryWindowStart: TimeValue
): boolean {
  const candidateGap = pickupToDeliveryGapMinutes(candidate.finish, deliveryWindowStart);
  const incumbentGap = pickupToDeliveryGapMinutes(incumbent.finish, deliveryWindowStart);
  if (candidateGap !== incumbentGap) {
    return candidateGap < incumbentGap;
  }
  return timeValueToMinutes(candidate.start) > timeValueToMinutes(incumbent.start);
}

// The site a delivery slot's deliveries are really made at, when its delegation label names
// a site other than the slot's own.
function delegatedDeliverySiteUid(
  schedule: ExtendedSiteSchedule,
  deliveryDelegationLabel: string | undefined
): string | undefined {
  if (isNil(deliveryDelegationLabel)) {
    return undefined;
  }
  return describedSiteUid(
    schedule.custom_data?.labels?.[deliveryDelegationLabel]?.trim(),
    schedule.siteUid
  );
}

// The value a job's preparing or ordering site is sent with: only a site that differs from
// the one its operation really happens at says anything.
function describedSiteUid(
  siteUid: string | null | undefined,
  operationSiteUid: string
): string | undefined {
  return !siteUid || siteUid === operationSiteUid ? undefined : siteUid;
}

interface PickupCandidate {
  schedule: ExtendedSiteSchedule;
  window: ScheduleWindowBounds;
  commodity: string;
}

export function makeRows(
  schedules: ExtendedSiteSchedule[],
  formData: FormData,
  allCommodityUids: string[],
  allJobKindUids: string[],
  semanticFlowKindUids: SemanticFlowKindUids,
  commodityAndJobKindColorOptions: CommodityAndJobKindColorOption[] = [],
  deliveryDelegationLabel?: string
): { rows: TableRow[]; cellOptions: CellOptions<TableRow>[] } {
  const cellOptions: CellOptions<TableRow>[] = [];
  const rows: TableRow[] = [];
  if (isNilOrEmpty(formData.flow_types)) {
    // We are sure that there is only one element in flow types
    return { rows, cellOptions };
  }
  const flowKindUid = formData.flow_types[0];

  // create one line per shared commodity and loading site for each delivery schedule
  for (const deliverySchedule of schedules.filter((sch) => {
    if (!isScheduleCompatibleWithDelivery(sch)) {
      return false;
    }
    // Check if delivery schedule is compatible with selected delivery sites
    const selectedDeliverySites = formData.delivery_site_uids;
    if (
      isDefinedAndNotEmpty(selectedDeliverySites) &&
      !selectedDeliverySites.includes(sch.siteUid)
    ) {
      return false;
    }

    if (
      flowKindUid == semanticFlowKindUids.delivery &&
      !sch.siteKinds.includes(SiteKind.CLIENT)
    ) {
      return false;
    }

    if (
      flowKindUid == semanticFlowKindUids.fromProvidersToWarehouses &&
      !sch.siteKinds.includes(SiteKind.WAREHOUSE)
    ) {
      return false;
    }

    if (
      flowKindUid == semanticFlowKindUids.reverseLogistics &&
      !sch.siteKinds.includes(SiteKind.WAREHOUSE)
    ) {
      return false;
    }

    return true;
  })) {
    const deliveryWindowStartAndFinish = makeStartAndFinishTimeValues(
      deliverySchedule.window.time_window
    );
    if (isNil(deliveryWindowStartAndFinish)) {
      continue;
    }
    const [deliveryWindowStart, deliveryWindowFinish] = deliveryWindowStartAndFinish;
    // A delegated slot is still matched, windowed and softened as the ordering site's own:
    // only the site the truck unloads at moves. That keeps the line on days the real
    // delivery site declares no slot of its own. Only a delivery is delegated: the returns
    // and collections unloaded at a delegating site stay there.
    const realDeliverySiteUid =
      flowKindUid === semanticFlowKindUids.delivery
        ? delegatedDeliverySiteUid(deliverySchedule, deliveryDelegationLabel)
        : undefined;

    const rawDeliveryCommodities = deliverySchedule.compatible_commodity_uids ?? [];
    const deliveryCommodities =
      rawDeliveryCommodities.length > 0 ? rawDeliveryCommodities : allCommodityUids;
    const selectedCommodities = formData.commodities;

    // Several loading windows of the same site can be feasible for a single delivery
    // window. Only the one closest to the delivery is a realistic option, so we keep one
    // line per loading site and commodity rather than one per feasible pair.
    const closestPickups = new Map<string, PickupCandidate>();

    for (const pickupSchedule of schedules.filter((pickupCandidate) => {
      // Check if pickup schedule is compatible with selected pickup sites
      const selectedPickupSites = formData.pickup_site_uids;
      if (
        isDefinedAndNotEmpty(selectedPickupSites) &&
        !selectedPickupSites.includes(pickupCandidate.siteUid)
      ) {
        return false;
      }

      if (
        flowKindUid == semanticFlowKindUids.delivery &&
        !pickupCandidate.siteKinds.includes(SiteKind.WAREHOUSE)
      ) {
        return false;
      }

      if (
        flowKindUid == semanticFlowKindUids.fromProvidersToWarehouses &&
        !pickupCandidate.siteIsProvider
      ) {
        return false;
      }

      if (
        flowKindUid == semanticFlowKindUids.reverseLogistics &&
        !pickupCandidate.siteKinds.includes(SiteKind.CLIENT)
      ) {
        return false;
      }

      return areMatchingSchedules(pickupCandidate, deliverySchedule);
    })) {
      const pickupWindowStartAndFinish = makeStartAndFinishTimeValues(
        pickupSchedule.window.time_window
      );
      if (isNil(pickupWindowStartAndFinish)) {
        continue;
      }
      const [pickupWindowStart, pickupWindowFinish] = pickupWindowStartAndFinish;

      // Do not match schedules if pickup window starts after the delivery window finishes
      if (
        pickupWindowStart.hours > deliveryWindowFinish.hours ||
        (pickupWindowStart.hours == deliveryWindowFinish.hours &&
          pickupWindowStart.minutes >= deliveryWindowFinish.minutes)
      ) {
        continue;
      }

      const rawPickupCommodities = pickupSchedule.compatible_commodity_uids ?? [];
      const pickupCommodities =
        rawPickupCommodities.length > 0 ? rawPickupCommodities : allCommodityUids;

      for (const commodity of deliveryCommodities) {
        if (
          isDefinedAndNotEmpty(selectedCommodities) &&
          !selectedCommodities.includes(commodity)
        ) {
          continue;
        }
        const shareThisCommodity = pickupCommodities.includes(commodity);
        if (!shareThisCommodity) {
          continue;
        }

        const candidate: PickupCandidate = {
          schedule: pickupSchedule,
          window: { start: pickupWindowStart, finish: pickupWindowFinish },
          commodity
        };
        // NUL separator: it cannot appear in a uid, so two keys never collide
        const key = `${pickupSchedule.siteUid}\u0000${commodity}`;
        const incumbent = closestPickups.get(key);
        if (
          isNil(incumbent) ||
          isPickupCloserToDelivery(candidate.window, incumbent.window, deliveryWindowStart)
        ) {
          closestPickups.set(key, candidate);
        }
      }
    }

    for (const {
      schedule: pickupSchedule,
      window: pickupWindow,
      commodity
    } of closestPickups.values()) {
      // Build dynamic nb_pallets Record based on job kinds
      const nb_pallets: Record<string, string> = {};

      let deliveryCompatible = deliverySchedule.compatible_job_kind_uids;
      if (!isNil(deliveryCompatible) && deliveryCompatible.length === 0) {
        deliveryCompatible = undefined;
      }
      let pickupCompatible = pickupSchedule.compatible_job_kind_uids;
      if (!isNil(pickupCompatible) && pickupCompatible.length === 0) {
        pickupCompatible = undefined;
      }

      const restrictedJobKindUids =
        !isNil(deliveryCompatible) && !isNil(pickupCompatible)
          ? deliveryCompatible.filter((uid) => pickupCompatible.includes(uid))
          : (deliveryCompatible ?? pickupCompatible ?? null);

      const row: TableRow = {
        uid: crypto.randomUUID(),
        pickup_site_uid: pickupSchedule.siteUid,
        pickup_window_start: pickupWindow.start,
        pickup_window_finish: pickupWindow.finish,
        // Inherit the softness (earliness/lateness) from the schedules that produced
        // this line so the generated operation windows replicate their site schedule
        // configuration.
        pickup_earliness_cfg: pickupSchedule.window.earliness_cfg ?? null,
        pickup_lateness_cfg: pickupSchedule.window.lateness_cfg ?? null,
        delivery_site_uid: realDeliverySiteUid ?? deliverySchedule.siteUid,
        ordering_site_uid: isNil(realDeliverySiteUid) ? null : deliverySchedule.siteUid,
        delivery_window_start: deliveryWindowStart,
        delivery_window_finish: deliveryWindowFinish,
        delivery_earliness_cfg: deliverySchedule.window.earliness_cfg ?? null,
        delivery_lateness_cfg: deliverySchedule.window.lateness_cfg ?? null,
        // Use first compatible flow kind UID, or undefined if unrestricted
        flow_type: flowKindUid ?? undefined,
        commodity,
        nb_pallets,
        assigned_transporter_uid: null,
        assigned_vehicle_uid: null,
        assigned_vehicle_registration: null
      };

      for (const uid of allJobKindUids) {
        const isDisabled =
          !isNil(restrictedJobKindUids) && !restrictedJobKindUids.includes(uid);
        const colorOption = commodityAndJobKindColorOptions.find(
          (opt) => opt.commodityUid === commodity && opt.jobKindUid === uid
        );
        const bgColor = colorOption?.hexColor;
        if (isDisabled || bgColor) {
          cellOptions.push({
            row,
            column: `nb_pallets.${uid}` as keyof TableRow,
            disabled: isDisabled
              ? { reason: 'Incompatible with schedules constraints', canStillEdit: true }
              : undefined,
            bgColor
          });
        }
      }

      rows.push(row);
    }
  }

  // Sorting is now handled by the sortedRows computed signal
  return { rows, cellOptions };
}

// Whether the selection is the only supported combination of several flow kinds: a
// delivery paired with the reverse logistics bringing the empties back.
export function isDeliveryAndReverseLogisticsSelection(
  flowTypes: string[] | undefined,
  semanticFlowKindUids: SemanticFlowKindUids
): boolean {
  return (
    !isNil(flowTypes) &&
    flowTypes.length == 2 &&
    flowTypes.includes(semanticFlowKindUids.delivery) &&
    flowTypes.includes(semanticFlowKindUids.reverseLogistics)
  );
}

// Build the grid lines for the selected flow kinds. A single flow kind is a plain
// filter-then-build pass; the delivery and reverse logistics combination is built as two
// independent single-flow passes, the reverse one swapping the loading and unloading
// sites, and their lines concatenated.
export function makeRowsForSelectedFlowTypes(
  schedules: ExtendedSiteSchedule[],
  formData: FormData,
  allCommodityUids: string[],
  allJobKindUids: string[],
  semanticFlowKindUids: SemanticFlowKindUids,
  commodityAndJobKindColorOptions: CommodityAndJobKindColorOption[] = [],
  deliveryDelegationLabel?: string
): { rows: TableRow[]; cellOptions: CellOptions<TableRow>[] } {
  const buildSingleFlowRows = (
    singleFlowFormData: FormData
  ): { rows: TableRow[]; cellOptions: CellOptions<TableRow>[] } =>
    makeRows(
      filterSchedules(schedules, singleFlowFormData, semanticFlowKindUids),
      singleFlowFormData,
      allCommodityUids,
      allJobKindUids,
      semanticFlowKindUids,
      commodityAndJobKindColorOptions,
      deliveryDelegationLabel
    );

  if (!isDeliveryAndReverseLogisticsSelection(formData.flow_types, semanticFlowKindUids)) {
    return buildSingleFlowRows(formData);
  }

  const { rows: deliveryRows, cellOptions: deliveryCellOptions } = buildSingleFlowRows({
    ...formData,
    flow_types: [semanticFlowKindUids.delivery]
  });

  const { rows: reverseRows, cellOptions: reverseCellOptions } = buildSingleFlowRows({
    ...formData,
    flow_types: [semanticFlowKindUids.reverseLogistics],
    pickup_site_uids: formData.delivery_site_uids,
    delivery_site_uids: formData.pickup_site_uids
  });

  return {
    rows: deliveryRows.concat(reverseRows),
    cellOptions: deliveryCellOptions.concat(reverseCellOptions)
  };
}

// Whether a row holds everything the generation needs to say what lines it stands for: it
// must be a draft the user added, with both the cells the expansion is driven by.
export function canExpandManualRow(row: TableRow): boolean {
  return (
    row.isManualDraft === true &&
    row.pickup_site_uid !== '' &&
    !isNil(row.flow_type) &&
    row.flow_type !== ''
  );
}

// Day the expansion matches schedule recurrences against: the one the generation form
// currently holds, exactly as pressing Generate would read it. The day a previous
// generation ran for is deliberately not consulted — the form is what the user is looking
// at, and a stale day silently evaluates the wrong weekday's schedules.
//
// An absent or unreadable day yields undefined, which keeps every window of the site
// eligible; passing one on would have `filterSchedules` build an invalid Date, which
// `matchDate` cannot compare and, on a one-off recurrence, throws on.
export function resolveFillReferenceDay(formDate: unknown): string | undefined {
  if (typeof formDate !== 'string' || formDate === '') {
    return undefined;
  }
  const parsed = new Date(formDate);
  return isNaN(parsed.getTime()) ? undefined : parsed.toISOString().split('T')[0];
}

// The generation form as it applies to a single added row. The row's flow kind and loading
// site replace the form's, and every cell the user typed on the row narrows the generation
// further, so a typed commodity yields one line per unloading site rather than several
// identical ones. The form's remaining fields are kept, so the lines match what pressing
// Generate would have produced.
export function buildManualRowFormData(
  row: TableRow,
  formData: FormData,
  referenceDay: string | undefined
): FormData {
  const scoped: FormData = {
    ...formData,
    date: referenceDay,
    flow_types: isNil(row.flow_type) ? undefined : [row.flow_type],
    pickup_site_uids: [row.pickup_site_uid]
  };
  if (row.delivery_site_uid !== '') {
    scoped.delivery_site_uids = [row.delivery_site_uid];
  }
  if (row.commodity !== '') {
    scoped.commodities = [row.commodity];
  }
  return scoped;
}

// Carry the cells the user typed on the draft onto the lines the generation produced, so
// the expansion adds what the row was missing without discarding what it already held.
// Mutates in place: the cell options built alongside those lines hold row object
// references, which copying the rows would orphan.
export function applyManualRowCells(produced: TableRow[], draft: TableRow): void {
  const typedQuantities = Object.entries(draft.nb_pallets).filter(
    ([, quantity]) => quantity !== ''
  );
  const keepsPickupWindow = !isDefaultWindow(
    draft.pickup_window_start,
    draft.pickup_window_finish
  );
  const keepsDeliveryWindow = !isDefaultWindow(
    draft.delivery_window_start,
    draft.delivery_window_finish
  );

  for (const row of produced) {
    if (keepsPickupWindow) {
      row.pickup_window_start = draft.pickup_window_start;
      row.pickup_window_finish = draft.pickup_window_finish;
    }
    if (keepsDeliveryWindow) {
      row.delivery_window_start = draft.delivery_window_start;
      row.delivery_window_finish = draft.delivery_window_finish;
    }
    if (!isNil(draft.assigned_transporter_uid)) {
      row.assigned_transporter_uid = draft.assigned_transporter_uid;
    }
    if (!isNil(draft.assigned_vehicle_uid)) {
      // The registration options are derived from the vehicle, so the two travel together.
      row.assigned_vehicle_uid = draft.assigned_vehicle_uid;
      row.assigned_vehicle_registration = draft.assigned_vehicle_registration;
    }
    if (draft.preparing_site_uid) {
      row.preparing_site_uid = draft.preparing_site_uid;
    }
    if (draft.ordering_site_uid) {
      row.ordering_site_uid = draft.ordering_site_uid;
    }
    for (const [jobKindUid, quantity] of typedQuantities) {
      row.nb_pallets[jobKindUid] = quantity;
    }
  }
}

/**
 * A second line for the same transport order, to be filled with its own quantities. Everything
 * that describes *which* order it is — sites, windows, softness, flow, commodity, assignments —
 * is what makes the copy worth having; only the quantities start over.
 *
 * The windows are copied by value because the cell editors write into them in place: a shared
 * `TimeValue` would let an edit on the copy rewrite its source's window.
 *
 * `isManualDraft` is dropped: a copy already carries a flow type and a loading site, so a truthy
 * flag would make `expandManualRow` replace it with generated lines on its first edit.
 */
export function duplicateRowWithoutQuantities(row: TableRow): TableRow {
  return {
    ...row,
    uid: crypto.randomUUID(),
    pickup_window_start: { ...row.pickup_window_start },
    pickup_window_finish: { ...row.pickup_window_finish },
    delivery_window_start: { ...row.delivery_window_start },
    delivery_window_finish: { ...row.delivery_window_finish },
    nb_pallets: Object.fromEntries(
      Object.keys(row.nb_pallets).map((jobKindUid) => [jobKindUid, ''])
    ),
    isManualDraft: false
  };
}

/**
 * The source row's cell options, re-pointed at its copy. The table resolves a cell option by row
 * reference, so without this the copy would lose the commodity colours and the
 * schedule-incompatibility disabling that `makeRows` gave its quantity cells.
 */
export function cloneCellOptionsForRow(
  options: CellOptions<TableRow>[],
  source: TableRow,
  copy: TableRow
): CellOptions<TableRow>[] {
  return options
    .filter((option) => option.row === source)
    .map((option) => ({ ...option, row: copy }));
}

// Sort order for site kinds: warehouses first, then suppliers, then clients. Any other
// kind (or a site with unknown kinds) sorts after these ranked kinds.
const SITE_KIND_SORT_ORDER: Record<string, number> = {
  [SiteKind.WAREHOUSE]: 0,
  [SiteKind.PROVIDER]: 1,
  [SiteKind.CLIENT]: 2
};

// Rank a site by the best (lowest) rank among its kinds. Sites without a ranked kind
// (or with no kind information at all) sort last.
export function siteKindRank(kinds: string[] | undefined): number {
  if (isNilOrEmpty(kinds)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return kinds.reduce((best, kind) => {
    const rank = SITE_KIND_SORT_ORDER[kind];
    return !isNil(rank) && rank < best ? rank : best;
  }, Number.MAX_SAFE_INTEGER);
}

/**
 * The Client-kind site a row serves. A delivery names it as its unloading site and a
 * reverse-logistics run as its loading site, so both ends have to be looked at — that is
 * what puts an `E02 → 0020` delivery and the `0020 → E02` return in the same band.
 * Null when neither end is a client (a supplier collection, or a site whose kinds are not
 * known yet); such a row carries no band.
 */
export function clientSiteUid(
  row: TableRow,
  siteKindsByUid: Record<string, string[]>
): string | null {
  // A delegated delivery serves the store that ordered it, not the site it unloads at. The
  // unloading site is still looked at, for an ordering site that is not a client.
  const ends = [row.ordering_site_uid, row.delivery_site_uid, row.pickup_site_uid].filter(
    (uid): uid is string => !!uid
  );
  // A depot can be tagged Client on top of Warehouse, so both ends of a run may carry the
  // kind. The store is then the end whose best-ranked kind is Client; otherwise a return to
  // such a depot would be banded under the depot instead of the store it leaves.
  const store = ends.find(
    (uid) => siteKindRank(siteKindsByUid[uid]) === SITE_KIND_SORT_ORDER[SiteKind.CLIENT]
  );
  return (
    store ?? ends.find((uid) => siteKindsByUid[uid]?.includes(SiteKind.CLIENT)) ?? null
  );
}

// Compare two sites first by kind (Warehouse → Supplier → Client) then by label (uid).
function compareSitesByKindThenLabel(
  siteUidA: string,
  siteUidB: string,
  siteKindsByUid: Record<string, string[]>
): number {
  const rankA = siteKindRank(siteKindsByUid[siteUidA]);
  const rankB = siteKindRank(siteKindsByUid[siteUidB]);
  if (rankA !== rankB) {
    return rankA - rankB;
  }
  return siteUidA.localeCompare(siteUidB);
}

/**
 * Minutes a row's window bound sorts at. A bound the stored checkpoint cannot produce a time
 * from sorts last instead of throwing: this comparator runs inside `sortedRows`, so one
 * unreadable value would otherwise take the whole grid down on every render — and, since the
 * checkpoint is reloaded from storage, keep taking it down after a refresh.
 */
function comparableMinutes(bound: unknown): number {
  const time = processTimeValue(bound);
  return isNil(time) || !('hours' in time)
    ? Number.MAX_SAFE_INTEGER
    : timeValueToMinutes(time);
}

// Compare two rows chronologically, earliest first: by loading window start, then by
// unloading window start.
function compareRowsChronologically(a: TableRow, b: TableRow): number {
  const pickupComparison =
    comparableMinutes(a.pickup_window_start) - comparableMinutes(b.pickup_window_start);
  if (pickupComparison !== 0) {
    return pickupComparison;
  }
  return (
    comparableMinutes(a.delivery_window_start) - comparableMinutes(b.delivery_window_start)
  );
}

/**
 * The window a row opens and closes at the client site it serves: a delivery is read through
 * its unloading window, a return through its loading one. Both ends of a return are windows
 * of the same run, but only the one at the client is the operation the store itself plans.
 */
function clientSiteWindow(row: TableRow, clientSite: string): [number, number] {
  return row.pickup_site_uid === clientSite
    ? [
        comparableMinutes(row.pickup_window_start),
        comparableMinutes(row.pickup_window_finish)
      ]
    : [
        comparableMinutes(row.delivery_window_start),
        comparableMinutes(row.delivery_window_finish)
      ];
}

// Order two rows of one client by the operation the client site runs them at: window start
// first, then window end, which is what separates a delivery from a return opening at the
// same hour. Rows the client site cannot tell apart fall back to the whole-run chronology.
function compareAtClientSite(a: TableRow, b: TableRow, clientSite: string): number {
  const [startA, finishA] = clientSiteWindow(a, clientSite);
  const [startB, finishB] = clientSiteWindow(b, clientSite);
  if (startA !== startB) {
    return startA - startB;
  }
  if (finishA !== finishB) {
    return finishA - finishB;
  }
  return compareRowsChronologically(a, b);
}

// Order by loading site (pickup) by kind then label, then apply the same ordering to the
// unloading site (delivery) as a tiebreaker. Rows sharing a site pair are then ordered
// chronologically, from the earliest window to the latest.
function compareBySitePair(
  a: TableRow,
  b: TableRow,
  siteKindsByUid: Record<string, string[]>
): number {
  const pickupComparison = compareSitesByKindThenLabel(
    a.pickup_site_uid,
    b.pickup_site_uid,
    siteKindsByUid
  );
  if (pickupComparison !== 0) {
    return pickupComparison;
  }
  const deliveryComparison = compareSitesByKindThenLabel(
    a.delivery_site_uid,
    b.delivery_site_uid,
    siteKindsByUid
  );
  if (deliveryComparison !== 0) {
    return deliveryComparison;
  }
  return compareRowsChronologically(a, b);
}

/**
 * Rows are grouped by the client site they serve — whichever end of the run touches it — and
 * ordered inside a group by the window at that site. A store's returns therefore sit among
 * its deliveries, in the order its own schedule runs them, rather than in a block of their
 * own; that is what the grid is read by, and what makes each colour band one contiguous run.
 *
 * A row naming no client site (a supplier collection, or any row whose site kinds have not
 * loaded yet) has no store to be grouped under, so it keeps the site-pair ordering and sorts
 * after every row that has one.
 */
export function defaultSortingFunction(
  a: TableRow,
  b: TableRow,
  siteKindsByUid: Record<string, string[]>
): number {
  const clientA = clientSiteUid(a, siteKindsByUid);
  const clientB = clientSiteUid(b, siteKindsByUid);

  if (isNil(clientA) || isNil(clientB)) {
    if (clientA !== clientB) {
      return isNil(clientA) ? 1 : -1;
    }
    return compareBySitePair(a, b, siteKindsByUid);
  }
  if (clientA !== clientB) {
    return clientA.localeCompare(clientB);
  }
  return compareAtClientSite(a, b, clientA);
}
